/* ─────────────────────────────────────────────────────────────
   SupervisorDashboard.jsx — DMU 614 / Sprint Camellones (e)
   Dashboard desktop para supervisores generales y zonales.
   Tabs: Intervenciones hoy · Auditoría · Alertas · Reporte diario
   ───────────────────────────────────────────────────────────── */
import React, { useEffect, useState, useMemo, useCallback, Component } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import ModoDemoBanner from "../components/ModoDemoBanner";
import WidgetTicketsAreas from "../components/WidgetTicketsAreas";
import PhotoPicker from "../components/PhotoPicker";
import { TabParquesContratos } from "./CapturistaModule";
import "../styles/supervisor.css";
import "../styles/modal-parque.css";

/* ── Error Boundary — atrapa crashes en render y los muestra en pantalla
   en vez de dejar una página en blanco (útil para diagnosticar errores admin). ── */
class SupervisorErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#FEF2F2", padding: 32 }}>
          <div style={{ maxWidth: 560, background: "white", border: "2px solid #FCA5A5", borderRadius: 16, padding: 24 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#DC2626", marginBottom: 8 }}>
              <i className="fa-solid fa-triangle-exclamation mr-2"/>Error al cargar Estatus de Flotilla
            </div>
            <p style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>
              Ocurrió un error interno. Copia el mensaje de abajo y repórtalo.
            </p>
            <pre style={{ fontSize: 11, background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: 12, overflowX: "auto", whiteSpace: "pre-wrap", color: "#374151" }}>
              {this.state.error?.toString()}{"\n"}{this.state.error?.stack}
            </pre>
            <button
              onClick={() => window.location.reload()}
              style={{ marginTop: 16, background: "#DC2626", color: "white", border: 0, padding: "8px 18px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
            >
              Recargar página
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const hoy = () => new Date().toISOString().slice(0, 10);

function SupervisorDashboardInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const esCapturista    = user?.rol === "capturista";
  const esControlTaller = user?.rol === "control_taller";
  const puedeVerActividadChoferes = ["admin", "supervisor_general_pipas", "supervisor_pipas", "capturista"].includes(user?.rol);
  const [tab, setTab] = useState(
    esCapturista ? "parques_contratados" : "hoy"
  );
  // Fuerza que control_taller siempre arranque en "hoy" con filtro "TODAS" (fix bug vista vacía)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [verDemo, setVerDemo] = useState(false);

  const esSupervisorZonal = user?.rol === "supervisor_areas" && !user?.atribuciones?.includes("autoriza_cobertura_cualquier_zona");

  // Tabs: capturista → solo "Parques Contratados 2026".
  //       control_taller → solo "Intervenciones hoy" (acceso a registrar pipa en taller).
  // mapa_zonas desactivado (datos viejos) — descomentar para reactivar:
  // ["mapa_zonas",  "Mapa de Zonas",        "fa-map-location-dot"],
  const TABS_TODOS = [
    ["hoy",                  esControlTaller ? "Flotilla" : "Intervenciones hoy", "fa-truck-droplet"],
    ["parques_contratados",  "Parques Contratados 2026","fa-map-location-dot"],
    ["asignacion",           "Asignación del día",      "fa-people-arrows"],
    ["auditoria",            "Auditoría histórica",     "fa-magnifying-glass-chart"],
    ["actividad",            "Actividad Choferes",      "fa-user-clock"],
    ["reporte",              "Reporte diario",          "fa-file-pdf"],
    ["catalogo",             "Catálogo de Espacios",    "fa-map-pin"],
    ["radiografia",          "Radiografía de Parques",  "fa-chart-bar"],
  ];
  const puedeVerRadiografia = ["admin","supervisor_general_pipas","capturista","coordinador_pipas","supervisor_pipas"].includes(user?.rol);
  const esSupervisorGeneralPipas = user?.rol === "supervisor_general_pipas";
  const tabsVisibles = TABS_TODOS.filter(([k]) => {
    if (k === "actividad")   return puedeVerActividadChoferes;
    if (k === "catalogo")    return user?.rol === "admin";
    if (k === "radiografia") return puedeVerRadiografia;
    // Pestañas ocultas para supervisor_general_pipas (Octavio 27-ago-2026)
    if (esSupervisorGeneralPipas && k === "asignacion") return false;
    if (esSupervisorGeneralPipas && k === "auditoria")  return false;
    if (esCapturista)        return k === "parques_contratados";
    if (esControlTaller)     return k === "hoy";
    return true;
  });

  return (
    <div className="sup-shell" data-testid="page-supervisor">
      <ModoDemoBanner verDemo={verDemo} setVerDemo={setVerDemo} />      <header className="sup-topbar">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/hub")} className="iconbtn-light" data-testid="btn-back-hub">
            <i className="fa-solid fa-arrow-left" />
          </button>
          <div>
            <div className="sup-title">Estatus de Flotilla</div>
            <div className="sup-sub">
              {user?.nombre} · <span className="text-sky-300">{user?.rol}</span>
              {esSupervisorZonal && <> · Z{user?.zona_id}</>}
            </div>
          </div>
        </div>
        <button onClick={logout} className="iconbtn-light" data-testid="btn-logout">
          <i className="fa-solid fa-right-from-bracket" />
        </button>
      </header>

      {/* sup-tabs: oculto para control_taller (tiene su propia barra de 4 secciones) */}
      {!esControlTaller && (
        <nav className="sup-tabs" data-testid="sup-tabs">
          {tabsVisibles.map(([k, label, icon]) => (
            <button key={k}
              className={`sup-tab ${tab === k ? "active" : ""}`}
              onClick={() => setTab(k)}
              data-testid={`tab-${k}`}>
              <i className={`fa-solid ${icon}`} /> {label}
            </button>
          ))}
        </nav>
      )}

      {/* Barra de navegación entre secciones — solo control_taller (Octavio 24-ago-2026) */}
      {esControlTaller && (
        <div style={{ background: "white", borderBottom: "1px solid #E2E8F0", padding: "0 16px", display: "flex", gap: 2 }}>
          {[
            { k: "flotilla",         lbl: "Flotilla",   ic: "fa-truck-droplet",     to: null,                                          color: "#0369A1" },
            { k: "catalogo",         lbl: "Catálogo",   ic: "fa-car",               to: "/admin/control-vehicular",                    color: "#4C1D95" },
            { k: "uso-diario",       lbl: "Uso diario", ic: "fa-clipboard-list",    to: "/admin/control-vehicular?tab=uso-diario",      color: "#0F766E" },
            { k: "historial-taller", lbl: "Historial",  ic: "fa-clock-rotate-left", to: "/admin/control-vehicular?tab=historial-taller",color: "#B45309" },
          ].map(({ k, lbl, ic, to, color }) => {
            const activo = k === "flotilla";
            return (
              <button
                key={k}
                type="button"
                onClick={() => to && navigate(to)}
                data-testid={`ct-nav-${k}`}
                style={{
                  border: 0,
                  padding: "10px 14px",
                  fontSize: 13, fontWeight: 700,
                  borderRadius: activo ? "6px 6px 0 0" : 0,
                  background: activo ? color : "transparent",
                  color: activo ? "white" : color,
                  borderBottom: activo ? `3px solid ${color}` : `3px solid transparent`,
                  cursor: to ? "pointer" : "default",
                  transition: "background 0.15s, color 0.15s",
                }}
                onMouseEnter={(e) => { if (!activo) e.currentTarget.style.background = color + "18"; }}
                onMouseLeave={(e) => { if (!activo) e.currentTarget.style.background = "transparent"; }}
              >
                <i className={`fa-solid ${ic} mr-1`}/>{lbl}
              </button>
            );
          })}
        </div>
      )}

      <main className="sup-main">
        {!esCapturista && <div style={{marginBottom: "1rem"}}><WidgetTicketsAreas/></div>}
        {tab === "hoy"                 && <TabIntervencionesHoy user={user} esZonal={esSupervisorZonal} verDemo={verDemo} esControlTaller={esControlTaller} />}
        {tab === "mapa_zonas"          && <TabMapaZonas user={user} />}
        {tab === "parques_contratados" && <TabParquesContratos />}
        {tab === "asignacion"          && <TabAsignacionDia user={user} />}
        {tab === "auditoria"   && <TabAuditoria user={user} />}
        {tab === "actividad"   && <TabActividadChoferes user={user} />}
        {tab === "reporte"     && <TabReporteDiario user={user} verDemo={verDemo} />}
        {tab === "catalogo"    && <TabCatalogoEspacios user={user} />}
        {tab === "radiografia" && <TabRadiografiaParques />}
      </main>
    </div>
  );
}

export default function SupervisorDashboard(props) {
  return (
    <SupervisorErrorBoundary>
      <SupervisorDashboardInner {...props} />
    </SupervisorErrorBoundary>
  );
}

/* ═══════════════════════════════════════════════════════════
   TAB 1 — Intervenciones HOY
   ═══════════════════════════════════════════════════════════ */
function TabIntervencionesHoy({ user, esZonal, verDemo, esControlTaller }) {
  const [pipas, setPipas] = useState([]);
  const [interv, setInterv] = useState([]);
  const [noAtendidos, setNoAtendidos] = useState([]);
  const [filtros, setFiltros] = useState({ zona: "", turno: "", estado: "", tipo_agua: "" });
  // control_taller usa filtros propios (depto + número de unidad) en lugar de zona/turno/agua
  const [filtroDepto, setFiltroDepto] = useState("");
  const [filtroNumUnidad, setFiltroNumUnidad] = useState("");
  const [filtroRapido, setFiltroRapido] = useState("TODAS");  // TODAS | TALLER | EN_RUTA | PRESTADAS
  // Si control_taller tenía "PRESTADAS" seleccionado, restablecer a "TODAS"
  useEffect(() => {
    if (esControlTaller && filtroRapido === "PRESTADAS") setFiltroRapido("TODAS");
  }, [esControlTaller, filtroRapido]);
  const [detalle, setDetalle] = useState(null);
  const [quitandoTaller, setQuitandoTaller] = useState(null); // pipa.id en curso

  // control_taller puede operar el taller (registrar entrada/salida)
  const puedeQuitarTaller = ["admin", "supervisor_general_pipas", "supervisor_pipas", "control_taller"].includes(user?.rol);
  const [panelTaller, setPanelTaller] = useState(null); // pipa abierta en panel
  const [modalSalidaTaller, setModalSalidaTaller] = useState(null); // salida rápida desde tarjeta

  const cargar = async () => {
    try {
      if (esControlTaller) {
        // control_taller: cargar catálogo completo de vehículos (no solo las 23 pipas)
        const [rv, ri] = await Promise.all([
          axios.get(`${API}/vehiculos`, { params: { limite: 2000, pagina: 1 } }),
          axios.get(`${API}/intervenciones/dia/${hoy()}`, { params: { ...filtros, incluir_demo: verDemo } }),
        ]);
        // Adaptar vehiculos_catalogo al shape de pipa que espera el UI
        const items = (rv.data?.items || []).map((v) => ({ ...v, id_pipa: v.no_economico, _isCatalog: true }));
        setPipas(items);
        setInterv(ri.data || []);
        setNoAtendidos([]);
      } else {
        const [rp, ri, rna] = await Promise.all([
          axios.get(`${API}/pipas`),
          axios.get(`${API}/intervenciones/dia/${hoy()}`, { params: { ...filtros, incluir_demo: verDemo } }),
          axios.get(`${API}/supervisor/puntos-no-atendidos`).catch(() => ({ data: { items: [] } })),
        ]);
        setPipas(rp.data || []);
        setInterv(ri.data || []);
        setNoAtendidos(rna.data?.items || []);
      }
    } catch (e) {
      toast.error("Error cargando datos: " + (e.response?.data?.detail || e.message));
    }
  };

  const [modalTaller, setModalTaller] = useState(null); // pipa para la que se abre el modal de entrar a taller

  const handleQuitarTaller = async (e, p) => {
    e.stopPropagation();
    e.preventDefault();
    const etiqueta = p._isCatalog ? `unidad #${p.no_economico}` : `pipa #${p.id_pipa}`;
    if (!window.confirm(`¿Confirmas que la ${etiqueta} ya salió del taller?`)) return;
    setQuitandoTaller(p.id || p.no_economico);
    try {
      if (p._isCatalog) {
        await axios.put(`${API}/vehiculos/${p.no_economico}/quitar-taller`, { fotos: [] });
        toast.success(`Unidad #${p.no_economico} regresada a operativa`);
      } else {
        await axios.put(`${API}/coberturas/pipa/${p.id || p.id_pipa}/quitar-taller`, { fotos: [] });
        toast.success(`Pipa #${p.id_pipa} regresada a operativa`);
      }
      await cargar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al quitar de taller");
    } finally {
      setQuitandoTaller(null);
    }
  };

  // Versión del panel: recibe fotos opcionales desde PanelTaller
  const handleQuitarTallerDesdePanel = async (p, fotos = []) => {
    const etiqueta = p._isCatalog ? `unidad #${p.no_economico}` : `pipa #${p.id_pipa}`;
    if (!window.confirm(`¿Confirmas que la ${etiqueta} ya salió del taller?`)) return;
    setQuitandoTaller(p.id || p.no_economico);
    try {
      if (p._isCatalog) {
        await axios.put(`${API}/vehiculos/${p.no_economico}/quitar-taller`, { fotos });
        toast.success(`Unidad #${p.no_economico} regresada a operativa`);
      } else {
        await axios.put(`${API}/coberturas/pipa/${p.id || p.id_pipa}/quitar-taller`, { fotos });
        toast.success(`Pipa #${p.id_pipa} regresada a operativa`);
      }
      await cargar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al quitar de taller");
    } finally {
      setQuitandoTaller(null);
    }
  };

  const handleEntrarTaller = (e, p) => {
    e.stopPropagation();
    e.preventDefault();
    setModalTaller(p);
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [filtros, verDemo]);

  const intervPorPipa = useMemo(() => {
    const m = {};
    for (const i of interv) m[i.pipa_numero] = i;
    return m;
  }, [interv]);

  // Defensivo: producción tiene docs con estado_operativo vacío/legacy y estado="EN_TALLER" (o viceversa).
  // Revisamos AMBOS campos en paralelo y aceptamos cualquier variante.
  const TALLER_VARIANTES = ["EN_TALLER", "TALLER", "EN TALLER", "EN-TALLER"];
  const normEst = (v) => String(v ?? "").toUpperCase().trim();
  const esTallerPipa = (p) => {
    if (p._isCatalog) return p.status === "TALLER";
    const a = normEst(p.estado_operativo);
    const b = normEst(p.estado);
    return TALLER_VARIANTES.includes(a) || TALLER_VARIANTES.includes(b)
        || a.includes("TALLER") || b.includes("TALLER");
  };
  const esPrestadaPipa = (p) => {
    if (p._isCatalog) return false;
    const a = normEst(p.estado_operativo);
    const b = normEst(p.estado);
    return a.startsWith("PRESTAD") || b.startsWith("PRESTAD");
  };

  // Lista dinámica de departamentos — derivada directamente del catálogo cargado (control_taller)
  // Para otros roles no se usa
  const departamentos = useMemo(() => {
    if (!esControlTaller) return [];
    const set = new Set();
    pipas.forEach((p) => { if (p.departamento) set.add(p.departamento); });
    return Array.from(set).sort();
  }, [pipas, esControlTaller]);

  const pipasFiltradas = useMemo(() => {
    return pipas.filter((p) => {
      // Filtros estándar (NO aplican para control_taller — ellos usan depto/numUnidad)
      if (!esControlTaller) {
        if (filtros.zona && p.zona_asignada !== filtros.zona && !p.zonas?.includes(filtros.zona)) return false;
        if (filtros.turno && p.turno !== filtros.turno) return false;
        if (filtros.tipo_agua && p.tipo_agua !== filtros.tipo_agua) return false;
        if (esZonal && user.zona_id && !p.zonas?.includes(user.zona_id)) return false;
      }
      // Filtros de control_taller
      if (esControlTaller) {
        // Las unidades del catálogo tienen p.departamento directamente
        if (filtroDepto && (p.departamento || "") !== filtroDepto) return false;
        if (filtroNumUnidad && !String(p.id_pipa ?? "").toLowerCase().includes(filtroNumUnidad.toLowerCase())) return false;
      }
      // Filtro rápido (pills) — se suma a los filtros existentes
      if (filtroRapido === "TALLER") {
        if (!esTallerPipa(p)) return false;
      } else if (filtroRapido === "PRESTADAS") {
        if (!esPrestadaPipa(p)) return false;
      } else if (filtroRapido === "EN_RUTA") {
        if (!intervPorPipa[p.id_pipa]) return false;  // tiene intervención hoy
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipas, filtros, esZonal, user, filtroRapido, intervPorPipa, esControlTaller, filtroDepto, filtroNumUnidad]);

  const colorPipa = (p) => {
    if (esTallerPipa(p)) return "#475569";
    const i = intervPorPipa[p.id_pipa];
    if (!i) return "#94a3b8";
    const cob = i.cobertura_pct || 0;
    if (i.estado === "EN_CURSO" && cob < 25) return "#dc2626";
    if (cob >= 75) return "#16a34a";
    return "#eab308";
  };

  return (
    <div data-testid="tab-hoy-panel">
      {/* Pills de filtro rápido (mobile-first) */}
      <div className="sup-quickfilter" data-testid="quickfilter-bar">
        {[
          { k: "TODAS",     lbl: "Todas",          ic: "fa-list",                  bg: "#475569" },
          { k: "TALLER",    lbl: "En taller",      ic: "fa-screwdriver-wrench",    bg: "#D97706" },
          { k: "EN_RUTA",   lbl: "En ruta",        ic: "fa-truck-droplet",         bg: "#16A34A" },
          ...(!esControlTaller ? [{ k: "PRESTADAS", lbl: "Prestadas", ic: "fa-arrow-right-arrow-left", bg: "#0EA5E9" }] : []),
        ].map(({ k, lbl, ic, bg }) => {
          const activo = filtroRapido === k;
          return (
            <button
              key={k}
              type="button"
              className="sup-qf-pill"
              data-testid={`qf-${k.toLowerCase()}`}
              data-active={activo}
              onClick={() => setFiltroRapido(k)}
              style={{
                background: activo ? bg : "#F1F5F9",
                color: activo ? "white" : "#334155",
                borderColor: activo ? bg : "#CBD5E1",
              }}
            >
              <i className={`fa-solid ${ic}`}/>{lbl}
            </button>
          );
        })}
        <span className="sup-qf-count" data-testid="qf-count">
          {pipasFiltradas.length} <span style={{opacity:0.7}}>de {pipas.length} {esControlTaller ? "unidades" : "pipas"}</span>
        </span>
      </div>

      {/* Filtros */}
      <div className="sup-filters" data-testid="filtros-barra">
        {esControlTaller ? (
          /* ── Filtros exclusivos para control_taller ── */
          <>
            <select
              value={filtroDepto}
              onChange={(e) => setFiltroDepto(e.target.value)}
              data-testid="filter-departamento"
            >
              <option value="">Departamento: todos</option>
              {departamentos.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <input
              type="text"
              placeholder="N° de unidad…"
              value={filtroNumUnidad}
              onChange={(e) => setFiltroNumUnidad(e.target.value)}
              data-testid="filter-num-unidad"
              style={{ border: "1px solid #CBD5E1", borderRadius: 6, padding: "4px 10px", fontSize: 13, minWidth: 120 }}
            />
          </>
        ) : (
          /* ── Filtros estándar (admin y otros roles) ── */
          <>
            <select value={filtros.zona} onChange={(e) => setFiltros({ ...filtros, zona: e.target.value })} data-testid="filter-zona">
              <option value="">Zona: todas</option>
              {[1,2,3,4,5,6,7,8,10,11,12,13,14].map((z) => <option key={z} value={`Z${z}`}>Z{z}</option>)}
              <option value="CENTRO">CENTRO</option>
            </select>
            <select value={filtros.turno} onChange={(e) => setFiltros({ ...filtros, turno: e.target.value })} data-testid="filter-turno">
              <option value="">Turno: todos</option>
              <option value="DIURNO">DIURNO</option>
              <option value="NOCTURNO">NOCTURNO</option>
            </select>
            <select value={filtros.estado} onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })} data-testid="filter-estado">
              <option value="">Estado: todos</option>
              <option value="EN_CURSO">EN_CURSO</option>
              <option value="COMPLETADA">COMPLETADA</option>
              <option value="PARCIAL">PARCIAL</option>
            </select>
            <select value={filtros.tipo_agua} onChange={(e) => setFiltros({ ...filtros, tipo_agua: e.target.value })} data-testid="filter-tipo-agua">
              <option value="">Agua: todas</option>
              <option value="TRATADA">TRATADA</option>
              <option value="POTABLE">POTABLE</option>
            </select>
          </>
        )}
        <button className="sup-btn-refresh" onClick={cargar} data-testid="btn-refresh"><i className="fa-solid fa-arrows-rotate" /> Refrescar</button>
        {puedeQuitarTaller && <BtnResumenTaller />}
      </div>

      <div className="sup-hoy">
      {/* Grid de unidades */}
      <div className="sup-grid-pipas" data-testid="grid-pipas">
        {pipasFiltradas.map((p) => {
          const i = intervPorPipa[p.id_pipa];
          const color = colorPipa(p);

          /* ── Tarjeta diseño Control Taller (genérica, escalable) ── */
          if (esControlTaller) {
            return (
              <TarjetaVehiculoCT
                key={p.id_pipa}
                pipa={p}
                esTaller={esTallerPipa(p)}
                esPrestada={esPrestadaPipa(p)}
                onVerTaller={() => setPanelTaller(p)}
                onEntrarTaller={(e) => handleEntrarTaller(e, p)}
                onSalirTaller={() => setModalSalidaTaller(p)}
              />
            );
          }

          /* ── Tarjeta rediseñada (admin / supervisores de pipas) ── */
          return (
            <TarjetaPipaSupervisor
              key={p.id_pipa}
              pipa={p}
              intervencion={i}
              esTaller={esTallerPipa(p)}
              esPrestada={esPrestadaPipa(p)}
              puedeAccionarTaller={puedeQuitarTaller}
              onVerDetalle={() => setDetalle({ pipa: p, intervencion: i })}
              onVerTaller={() => setPanelTaller(p)}
              onEntrarTaller={(e) => handleEntrarTaller(e, p)}
              onSalirTaller={() => setModalSalidaTaller(p)}
            />
          );
        })}
      </div>

      {/* Puntos no atendidos hoy (chofer reportó motivo) */}
      {noAtendidos.length > 0 && (
        <div className="sup-noatend" data-testid="lista-no-atendidos" style={{ marginBottom: 12 }}>
          <div className="sup-list-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <i className="fa-solid fa-circle-exclamation" style={{ color: "#DC2626" }}/>
            Puntos no atendidos ({noAtendidos.length})
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8, maxHeight: 240, overflowY: "auto" }}>
            {noAtendidos.map((p) => (
              <li key={p.id} data-testid={`no-atend-${p.id}`}
                  style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderLeft: "4px solid #DC2626", borderRadius: 8, padding: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {p.zona_id && <span style={{ background: "#EEF2FF", color: "#1E40AF", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 800 }}>{p.zona_id}</span>}
                  <strong style={{ fontSize: 13, color: "#0F172A" }}>{p.espacio_nombre || p.clave_ruta}</strong>
                  <span style={{ marginLeft: "auto", fontSize: 11, color: "#64748B" }}>Pipa #{p.pipa_id || "—"}</span>
                </div>
                <div style={{ fontSize: 12, color: "#9A3412", marginTop: 4, fontWeight: 700 }}>
                  <i className="fa-solid fa-triangle-exclamation"/> {p.motivo_no_atencion || "Sin motivo (legado)"}
                </div>
                {p.comentario && <div style={{ fontSize: 11, color: "#7F1D1D", marginTop: 2 }}>“{p.comentario}”</div>}
                <div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>{p.chofer_nombre || "—"} · {new Date(p.timestamp).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Lista lateral — solo para admin/supervisores de pipas; no aplica a control_taller */}
      {!esControlTaller && (
        <div className="sup-list-side">
          <div className="sup-list-title">Intervenciones del día ({interv.length})</div>
          <table className="sup-table" data-testid="tabla-intervenciones">
            <thead>
              <tr><th>Pipa</th><th>Espacio</th><th>Estado</th><th>Cob%</th><th>Vueltas</th></tr>
            </thead>
            <tbody>
              {interv.map((i) => (
                <tr key={i.id} onClick={() => {
                  const p = pipas.find((x) => x.id_pipa === i.pipa_numero);
                  setDetalle({ pipa: p, intervencion: i });
                }} className="cursor-pointer hover:bg-slate-50" data-testid={`row-int-${i.id}`}>
                  <td>{i.pipa_numero}{i.es_demo && <span className="demo-badge ml-1">🧪 DEMO</span>}{i.origen_captura === "CAPTURISTA_WHATSAPP" && <span className="badge-wa ml-1" data-testid={`badge-wa-${i.id}`} title={`Capturada por ${i.capturado_por_nombre || "capturista"}`}><i className="fa-brands fa-whatsapp"/></span>}</td>
                  <td className="truncate max-w-[180px]">
                    {i.espacio_nombre}
                    {i.sin_match_bd && (
                      <span title="Lugar escrito a mano — no encontrado en el catálogo de espacios" style={{marginLeft:4,fontSize:"0.65rem",background:"#FEF3C7",color:"#92400E",borderRadius:4,padding:"1px 4px",fontWeight:600,whiteSpace:"nowrap"}}>
                        ✏️ sin catálogo
                      </span>
                    )}
                  </td>
                  <td><span className={`pill-tiny pill-${i.estado.toLowerCase()}`}>{i.estado}</span></td>
                  <td>{i.cobertura_pct || 0}%</td>
                  <td>{i.total_vueltas || 0}</td>
                </tr>
              ))}
              {interv.length === 0 && <tr><td colSpan="5" className="text-center text-slate-400 py-4">Sin intervenciones hoy</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {detalle && <ModalDetalle {...detalle} onClose={() => setDetalle(null)} />}
      {panelTaller && (
        <PanelTaller
          pipa={panelTaller}
          user={user}
          onClose={() => setPanelTaller(null)}
          onQuitarTaller={async (fotos) => {
            await handleQuitarTallerDesdePanel(panelTaller, fotos);
            setPanelTaller(null);
          }}
        />
      )}
      {modalSalidaTaller && (
        <ModalSalidaTaller
          pipa={modalSalidaTaller}
          onClose={() => setModalSalidaTaller(null)}
          onConfirmado={async (fotos) => {
            await handleQuitarTallerDesdePanel(modalSalidaTaller, fotos);
            setModalSalidaTaller(null);
          }}
        />
      )}
      {modalTaller && (
        <ModalEntrarTaller
          pipa={modalTaller}
          onClose={() => setModalTaller(null)}
          onConfirmado={async () => {
            setModalTaller(null);
            await cargar();
          }}
        />
      )}
    </div>
  );
}

/* Modal detalle pipa/intervención */
function ModalDetalle({ pipa, intervencion, onClose }) {
  const [vueltas, setVueltas] = useState([]);
  const [cobertura, setCobertura] = useState(null);
  const [subpuntos, setSubpuntos] = useState([]);
  const [coberturaTemporal, setCoberturaTemporal] = useState(null);

  useEffect(() => {
    const cargar = async () => {
      if (intervencion) {
        const r = await axios.get(`${API}/intervenciones/${intervencion.id}`);
        setVueltas(r.data.vueltas || []);
        const sp = await axios.get(`${API}/espacios/${intervencion.espacio_id}/subpuntos`);
        setSubpuntos(sp.data || []);
        if (intervencion.es_cobertura && intervencion.asignacion_temporal_id) {
          try {
            const at = await axios.get(`${API}/asignaciones-temporales/activas`);
            const found = (at.data || []).find((x) => x.id === intervencion.asignacion_temporal_id);
            if (found) setCoberturaTemporal(found);
          } catch {}
        }
      }
    };
    cargar();
  }, [intervencion]);

  const tiempoEnCurso = intervencion?.fecha_inicio_iso
    ? Math.round((Date.now() - new Date(intervencion.fecha_inicio_iso)) / 60000) : 0;

  return (
    <div className="sup-modal-bg" onClick={onClose} data-testid="modal-detalle">
      <div className="sup-modal" onClick={(e) => e.stopPropagation()}>
        <header className="sup-modal-head">
          <div>
            <div className="text-2xl font-bold">PIPA #{pipa?.id_pipa}</div>
            <div className="text-sm text-slate-500">
              {pipa?.chofer_titular_nombre || pipa?.chofer_nombre || "Sin titular"}
              {intervencion && <> — {intervencion.espacio_nombre}</>}
            </div>
          </div>
          <button onClick={onClose} className="iconbtn" data-testid="btn-cerrar-modal"><i className="fa-solid fa-xmark" /></button>
        </header>

        {!intervencion && (
          <div className="p-6 text-center text-slate-500">Sin intervención activa hoy.</div>
        )}

        {intervencion && (
          <>
            <div className="sup-modal-stats">
              <div><i className="fa-regular fa-clock" /> {tiempoEnCurso} min</div>
              <div><i className="fa-solid fa-rotate" /> {intervencion.total_vueltas} vueltas</div>
              <div><i className="fa-solid fa-droplet" /> {intervencion.litros_totales} L</div>
              <div><i className="fa-solid fa-bullseye" /> {intervencion.cobertura_pct || 0}% cobertura</div>
            </div>

            {/* Cobertura / asignación temporal */}
            {intervencion.es_cobertura && (
              <div className="sup-modal-cobertura" data-testid="badge-cobertura-detalle">
                <i className="fa-solid fa-circle-info" />
                <div>
                  <div><strong>Operada hoy por:</strong> {intervencion.chofer_nombre} (cobertura)</div>
                  <div><strong>Pipa operada:</strong> {intervencion.pipa_numero}
                    {coberturaTemporal && <> — Titular: <code>{coberturaTemporal.notas?.includes("titular") ? "" : ""}</code></>}
                  </div>
                  {coberturaTemporal && (
                    <>
                      <div><strong>Motivo:</strong> {coberturaTemporal.motivo}</div>
                      <div className="text-xs text-slate-500">{coberturaTemporal.notas}</div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Capturada por Yanett (plan B WhatsApp) */}
            {intervencion.origen_captura === "CAPTURISTA_WHATSAPP" && (
              <div className="sup-modal-cobertura" data-testid="badge-captura-whatsapp"
                style={{background:"#FFF7ED", borderColor:"#FDBA74", color:"#7C2D12"}}>
                <i className="fa-solid fa-whatsapp" />
                <div>
                  <div><strong>Capturada por {intervencion.capturado_por_nombre || "capturista"}</strong> (sin GPS de campo)</div>
                  <div className="text-xs">Origen: reporte por WhatsApp · ubicación heredada del catálogo</div>
                  {intervencion.es_provisional && (
                    <div className="text-xs" style={{color:"#9A3412"}}><i className="fa-solid fa-triangle-exclamation mr-1"/>Lugar provisional pendiente de validar</div>
                  )}
                </div>
              </div>
            )}

            <div className="text-xs text-slate-500 mt-3">
              Calidad: {vueltas.filter((v) => v.metodo_seleccion === "GPS_AUTO").length} GPS_AUTO ·{" "}
              {vueltas.filter((v) => v.metodo_seleccion === "TOCK_MANUAL").length} TOCK_MANUAL ·{" "}
              {vueltas.filter((v) => v.metodo_seleccion === "GPS_CORREGIDO_MANUAL").length} GPS_CORREGIDO_MANUAL
            </div>

            <div className="sup-modal-sub">Detalle de sub-puntos:</div>
            <ul className="sup-modal-subs" data-testid="lista-subpuntos">
              {subpuntos.map((s) => {
                const v = vueltas.find((x) => x.subpunto_id === s.id);
                return (
                  <li key={s.id} className={v ? "done" : "pending"}>
                    {v ? <i className="fa-solid fa-check text-emerald-600" /> : <i className="fa-regular fa-square text-slate-300" />}
                    <span>{s.nombre}</span>
                    {v && <span className="text-xs text-slate-500">— {v.litros}L · {v.metodo_seleccion}</span>}
                  </li>
                );
              })}
              {!subpuntos.length && <li className="text-slate-400 text-sm">Espacio sin sub-puntos</li>}
            </ul>

            <footer className="sup-modal-foot">
              <button className="btn-ghost-sm" disabled><i className="fa-regular fa-images" /> Ver fotos (TODO)</button>
              <button className="btn-ghost-sm" disabled><i className="fa-solid fa-flag" /> Marcar incidencia</button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TAB 2 — Auditoría histórica
   ═══════════════════════════════════════════════════════════ */
function descargarCSV(filename, headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))];
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function TabAuditoria({ user }) {
  const [espacioId, setEspacioId] = useState("");
  const [data, setData] = useState(null);
  const [desde, setDesde] = useState((() => { const d = new Date(); d.setDate(d.getDate()-30); return d.toISOString().slice(0,10); })());
  const [hasta, setHasta] = useState(hoy());
  const [espacios, setEspacios] = useState([]);
  const [filtroTexto, setFiltroTexto] = useState("");

  useEffect(() => {
    axios.get(`${API}/espacios`, { params: { limit: 500 } }).then((r) => {
      setEspacios((r.data || []).filter((e) => e.tiene_subpuntos || e.permite_intervencion_continua));
    }).catch(() => {});
  }, []);

  const consultar = async () => {
    if (!espacioId) return toast.error("Selecciona un espacio");
    try {
      const r = await axios.get(`${API}/espacios/${espacioId}/cobertura`, { params: { desde, hasta } });
      setData(r.data);
      setFiltroTexto("");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const subpuntosFiltrados = data
    ? (data.subpuntos || []).filter((s) =>
        !filtroTexto || (s.nombre || "").toLowerCase().includes(filtroTexto.toLowerCase())
      )
    : [];

  const espacioNombre = (espacios.find((e) => e.id === espacioId) || {}).nombre || espacioId;

  const descargar = () => {
    if (!data) return;
    descargarCSV(
      `auditoria_${espacioNombre}_${desde}_${hasta}.csv`,
      ["Sub-punto", "% atendido", "Vueltas", "Última atención", "Días sin atención", "Métodos"],
      subpuntosFiltrados.map((s) => [
        s.nombre,
        s.pct_intervenciones + "%",
        s.total_vueltas,
        s.ultima_atencion || "",
        s.dias_sin_atencion ?? "",
        Object.entries(s.metodos || {}).map(([k, v]) => `${k}:${v}`).join(" | "),
      ])
    );
  };

  return (
    <div className="sup-pane" data-testid="tab-auditoria-panel">
      <h2 className="sup-h2">Auditoría histórica por espacio</h2>
      <div className="sup-filters">
        <select value={espacioId} onChange={(e) => setEspacioId(e.target.value)} data-testid="select-espacio">
          <option value="">Selecciona un espacio…</option>
          {espacios.map((e) => <option key={e.id} value={e.id}>{e.nombre} ({e.zona_id})</option>)}
        </select>
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="input-desde" />
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="input-hasta" />
        <button className="btn-primary" onClick={consultar} data-testid="btn-consultar">Consultar</button>
      </div>

      {data && (
        <div className="sup-aud-result" data-testid="audit-result">
          <div className="sup-card"><div className="sup-card-num">{data.total_intervenciones}</div><div className="sup-card-lbl">Intervenciones en rango</div></div>
          <div className="sup-filters mt-3">
            <input
              type="text"
              placeholder="Filtrar por sub-punto…"
              value={filtroTexto}
              onChange={(e) => setFiltroTexto(e.target.value)}
              className="border rounded p-1 flex-1 min-w-0"
              data-testid="input-filtro-auditoria"
            />
            <button className="btn-ghost-sm" onClick={descargar} title="Descargar CSV" data-testid="btn-descargar-auditoria">
              ⬇ CSV
            </button>
          </div>
          <table className="sup-table mt-2">
            <thead>
              <tr><th>Sub-punto</th><th>% atendido</th><th>Vueltas</th><th>Última</th><th>Días sin</th><th>Métodos</th></tr>
            </thead>
            <tbody>
              {subpuntosFiltrados.map((s) => (
                <tr key={s.subpunto_id} className={s.dias_sin_atencion > 14 ? "row-warn" : ""} data-testid={`row-sp-${s.subpunto_id}`}>
                  <td>{s.nombre}</td>
                  <td>{s.pct_intervenciones}%</td>
                  <td>{s.total_vueltas}</td>
                  <td>{s.ultima_atencion || "—"}</td>
                  <td>{s.dias_sin_atencion ?? "—"} {s.dias_sin_atencion > 14 && <span className="text-red-600">⚠ abandonada</span>}</td>
                  <td className="text-xs">{Object.entries(s.metodos || {}).map(([k, v]) => `${k}:${v}`).join(" · ") || "—"}</td>
                </tr>
              ))}
              {subpuntosFiltrados.length === 0 && (
                <tr><td colSpan="6" className="text-center text-slate-400 py-4">Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TAB 3 — Alertas
   ═══════════════════════════════════════════════════════════ */
function TabAlertas() {
  const [data, setData] = useState(null);
  const [umbral, setUmbral] = useState(7);
  const [filtroEspacio, setFiltroEspacio] = useState("");

  const cargar = async () => {
    try {
      const r = await axios.get(`${API}/alertas/subpuntos-desatendidos`, { params: { umbral_dias: umbral } });
      setData(r.data);
      setFiltroEspacio("");
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [umbral]);

  const itemsFiltrados = data
    ? (data.items || []).filter((it) =>
        !filtroEspacio || (it.espacio_nombre || "").toLowerCase().includes(filtroEspacio.toLowerCase())
      )
    : [];

  const descargar = () => {
    if (!data) return;
    descargarCSV(
      `alertas_desatendidos_${umbral}d_${hoy()}.csv`,
      ["Espacio", "Sub-punto", "Última atención", "Días sin atención"],
      itemsFiltrados.map((it) => [it.espacio_nombre, it.nombre, it.ultima_atencion || "Nunca", it.dias_sin_atencion ?? ""])
    );
  };

  return (
    <div className="sup-pane" data-testid="tab-alertas-panel">
      <h2 className="sup-h2">Alertas — sub-puntos desatendidos</h2>
      <div className="sup-filters">
        <label className="text-sm">Umbral (días):</label>
        <input type="number" value={umbral} onChange={(e) => setUmbral(parseInt(e.target.value || 7))} className="border rounded p-1 w-20" data-testid="input-umbral" />
        <button className="btn-primary" onClick={cargar} data-testid="btn-cargar-alertas">Recargar</button>
      </div>

      {data && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-lg font-semibold">{data.total} sub-puntos &gt; {data.umbral_dias} días sin atención</span>
            <input
              type="text"
              placeholder="Filtrar por espacio…"
              value={filtroEspacio}
              onChange={(e) => setFiltroEspacio(e.target.value)}
              className="border rounded p-1 flex-1 min-w-0"
              data-testid="input-filtro-alertas"
            />
            <button className="btn-ghost-sm" onClick={descargar} title="Descargar CSV" data-testid="btn-descargar-alertas">
              ⬇ CSV
            </button>
          </div>
          <table className="sup-table" data-testid="tabla-alertas">
            <thead><tr><th>Espacio</th><th>Sub-punto</th><th>Última atención</th><th>Acción</th></tr></thead>
            <tbody>
              {itemsFiltrados.map((it) => (
                <tr key={it.subpunto_id} data-testid={`row-alerta-${it.subpunto_id}`}>
                  <td>{it.espacio_nombre}</td>
                  <td>{it.nombre}</td>
                  <td>{it.ultima_atencion || <span className="text-red-600">Nunca</span>}</td>
                  <td><button className="btn-ghost-sm" data-testid={`btn-notificar-${it.subpunto_id}`} onClick={() => toast.info("TODO P2 — notificar al chofer por WhatsApp")}>Notificar chofer</button></td>
                </tr>
              ))}
              {itemsFiltrados.length === 0 && <tr><td colSpan="4" className="text-center text-slate-400 py-4">
                {filtroEspacio ? "Sin resultados para ese filtro" : "Sin alertas — todo al día 🎉"}
              </td></tr>}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TAB 4 — Reporte diario (para Director Rubén)
   ═══════════════════════════════════════════════════════════ */
function TabReporteDiario({ verDemo }) {
  const [fecha, setFecha] = useState(hoy());
  const [data, setData] = useState(null);

  const cargar = async () => {
    try {
      const r = await axios.get(`${API}/supervisor/reporte-diario`, { params: { fecha, incluir_demo: verDemo } });
      setData(r.data);
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [verDemo, fecha]);

  if (!data) return <div className="p-8 text-center">Cargando reporte…</div>;
  const r = data.resumen || {};
  const al = data.alertas || {};
  const p = data.pendientes || {};
  const cm = data.cumplimiento_mes || {};
  const desat = al.espacios_sin_regar_14dias || { total: 0, items: [] };
  const lit = r.litros_totales || { total: 0, tratada: 0, potable: 0 };
  const cumpCol = r.cumplimiento_estado === "CUMPLIDO" ? "#16A34A"
                : r.cumplimiento_estado === "PARCIAL"  ? "#D97706"
                : r.cumplimiento_estado === "SUSPENDIDO" ? "#64748B" : "#DC2626";

  return (
    <div className="sup-pane sup-reporte" data-testid="tab-reporte-panel">
      <div className="sup-filters">
        <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="input-fecha-reporte" />
        <button className="btn-primary" onClick={cargar}>Generar</button>
        <button className="btn-ghost-sm" disabled title="TODO Fase 2 — exportación PDF"><i className="fa-solid fa-file-pdf" /> Exportar PDF (TODO)</button>
        <button className="btn-ghost-sm" disabled title={data._envio_automatico?.razon || "TODO Fase 2 — envío automático"}><i className="fa-brands fa-whatsapp" /> Enviar WhatsApp (TODO)</button>
      </div>

      <div className="sup-reporte-card">
        <h2 className="sup-h2">📊 Reporte diario — {fecha} <span className="text-base font-normal text-slate-500">· {data.dia_semana}</span></h2>
        <div className="text-sm text-slate-500 mb-4">
          Generado: {new Date(data.generado_en).toLocaleString("es-MX")} por {data.generado_por || "—"}
        </div>

        {/* Cumplimiento del día */}
        <section data-testid="rep-cumplimiento" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 12, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ background: cumpCol, color: "white", padding: "4px 10px", borderRadius: 99, fontWeight: 800, fontSize: 12 }}>
              {r.cumplimiento_estado || "—"}
            </span>
            <strong style={{ fontSize: 18 }}>{(r.cumplimiento_pct ?? 0).toFixed(1)}%</strong>
            <span className="text-sm text-slate-600">{r.cumplimiento_nota || ""}</span>
          </div>
        </section>

        <section data-testid="rep-pipas">
          <h3>🚚 Pipas ({r.pipas_total ?? 0})</h3>
          <div className="rep-grid">
            <div>Activas: <strong>{r.pipas_activas ?? 0}</strong></div>
            <div>En taller: <strong>{r.pipas_taller ?? 0}</strong></div>
            <div>Prestadas: <strong>{r.pipas_prestadas ?? 0}</strong></div>
          </div>
          {(r.coberturas_del_dia || []).length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div className="text-xs text-slate-500 mb-1">Coberturas / préstamos del día:</div>
              {(r.coberturas_del_dia || []).slice(0, 12).map((c, i) => (
                <div key={i} className="rep-row text-sm">
                  · Pipa #{c.pipa} — {c.chofer_cobertura} <span className="text-slate-500">({c.motivo} · {c.zona})</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section data-testid="rep-interv" style={{ marginTop: 14 }}>
          <h3>💧 Intervenciones del día ({r.intervenciones_total ?? 0})</h3>
          <div className="rep-grid">
            <div>Completas: <strong>{r.intervenciones_completadas ?? 0}</strong></div>
            <div>Parciales: <strong>{r.intervenciones_parciales ?? 0}</strong></div>
            <div>En curso: <strong>{r.intervenciones_en_curso ?? 0}</strong></div>
            <div>Espacios regados: <strong>{r.espacios_regados ?? 0}</strong></div>
          </div>
          <div className="text-sm text-slate-600 mt-2">
            Litros: total <strong>{(lit.total || 0).toLocaleString("es-MX")}</strong> ·
            tratada <strong>{(lit.tratada || 0).toLocaleString("es-MX")}</strong> ·
            potable <strong>{(lit.potable || 0).toLocaleString("es-MX")}</strong>
          </div>
          {data.detalle?.por_pipa && Object.keys(data.detalle.por_pipa).length > 0 && (
            <table className="sup-table mt-2">
              <thead><tr><th>Pipa</th><th>Completas</th><th>Parciales</th><th>En curso</th><th>Litros</th></tr></thead>
              <tbody>
                {Object.entries(data.detalle.por_pipa).map(([k, v]) => (
                  <tr key={k}><td>{k}</td><td>{v.completas ?? 0}</td><td>{v.parciales ?? 0}</td><td>{v.en_curso ?? 0}</td><td>{(v.litros ?? 0).toLocaleString("es-MX")}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section data-testid="rep-pendientes" style={{ marginTop: 14 }}>
          <h3>📋 Pendientes</h3>
          <div className="rep-grid">
            <div>Solicitudes de pipa: <strong>{p.solicitudes_pipa_pendientes ?? 0}</strong></div>
            <div>Solicitudes ciudadanas (CRC): <strong>{p.solicitudes_ciudadanas_pendientes ?? 0}</strong></div>
            <div>Reportes de áreas abiertos: <strong>{p.reportes_areas_abiertos ?? 0}</strong></div>
          </div>
        </section>

        <section data-testid="rep-alertas" style={{ marginTop: 14 }}>
          <h3>🚨 Alertas del día</h3>
          {al.suspension_del_dia && (
            <div className="rep-row" style={{ background: "#FEF3C7", borderRadius: 6, padding: 6, marginBottom: 6 }}>
              <strong>Suspensión:</strong> {al.suspension_del_dia.motivo || "—"}
            </div>
          )}
          {(al.pipas_en_taller || []).length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div className="text-xs text-slate-500">Pipas en taller:</div>
              {al.pipas_en_taller.map((t, i) => (
                <div key={i} className="rep-row text-sm">· #{t.id_pipa} ({t.zona}) — desde {t.desde ? new Date(t.desde).toLocaleDateString("es-MX") : "—"}</div>
              ))}
            </div>
          )}
          {(al.pipas_prestadas || []).length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div className="text-xs text-slate-500">Pipas prestadas:</div>
              {al.pipas_prestadas.map((t, i) => (
                <div key={i} className="rep-row text-sm">· #{t.id_pipa} — {t.zona_origen} → {t.zona_destino}</div>
              ))}
            </div>
          )}
          {(al.camellones_parciales || []).length > 0 && (
            <div>
              <div className="text-xs text-slate-500">Camellones parciales:</div>
              {al.camellones_parciales.map((c, i) => (
                <div key={i} className="rep-row text-sm">· {c.espacio} ({c.zona}) — {c.cobertura_pct}%</div>
              ))}
            </div>
          )}
          {!al.suspension_del_dia && (al.pipas_en_taller || []).length === 0
            && (al.pipas_prestadas || []).length === 0 && (al.camellones_parciales || []).length === 0 && (
            <div className="text-slate-400 text-sm">Sin alertas hoy.</div>
          )}
        </section>

        <section data-testid="rep-desat" style={{ marginTop: 14 }}>
          <h3>🔔 Espacios sin regar &gt;14 días ({desat.total || 0})</h3>
          {(desat.items || []).slice(0, 10).map((d, i) => (
            <div key={i} className="rep-row">· {d.espacio} → {d.subpunto || "—"} (última: {d.ultima || "nunca"}{d.dias_sin ? ` · ${d.dias_sin} días` : ""})</div>
          ))}
          {(desat.total || 0) > 10 && <div className="text-xs text-slate-500 mt-1">+{(desat.total || 0) - 10} más…</div>}
          {(desat.total || 0) === 0 && <div className="text-slate-400 text-sm">Ninguno</div>}
        </section>

        <section data-testid="rep-bajo" style={{ marginTop: 14 }}>
          <h3>📉 Bajo desempeño (últimos 7 días)</h3>
          {(al.bajo_desempeno_7d || []).length === 0 && <div className="text-slate-400 text-sm">Ninguno</div>}
          {(al.bajo_desempeno_7d || []).map((b, i) => (
            <div key={i} className="rep-row">· {b.espacio} ({b.zona}) — {b.intervenciones_7d} interv · {b.cobertura_promedio_pct}% promedio</div>
          ))}
        </section>

        <section data-testid="rep-mes" style={{ marginTop: 14 }}>
          <h3>📅 Cumplimiento del mes ({cm.mes || "—"})</h3>
          <div className="text-sm text-slate-600">
            Días suspendidos: <strong>{cm.dias_suspendidos ?? 0}</strong>
            {cm.nota && <span className="block text-xs mt-1">{cm.nota}</span>}
          </div>
        </section>
      </div>
    </div>
  );
}



/* ═══════════════════════════════════════════════════════════
   PanelTaller — Bitácora colaborativa de una pipa EN_TALLER
   ═══════════════════════════════════════════════════════════ */
function PanelTaller({ pipa, user, onClose, onQuitarTaller }) {
  const [data, setData] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [nota, setNota] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [fotosSalida, setFotosSalida] = useState([]);
  // Modal foto — mismo patrón que TabActividadChoferes
  const [fotoModal, setFotoModal] = useState(null); // { url, fotos[], titulo }
  const puedeQuitar = ["admin", "supervisor_general_pipas", "control_taller"].includes(user?.rol);
  const puedeEscribir = ["admin", "supervisor_general_pipas", "supervisor_pipas", "control_taller"].includes(user?.rol);

  const cargar = async () => {
    try {
      const [rBit, rHist] = await Promise.all([
        axios.get(`${API}/coberturas/pipa/${pipa.id || pipa.id_pipa}/bitacora`),
        axios.get(`${API}/coberturas/pipa/${pipa.id || pipa.id_pipa}/historial-taller`),
      ]);
      setData(rBit.data);
      setHistorial(rHist.data?.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error cargando bitácora"); }
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [pipa.id]);

  const enviar = async () => {
    if (!nota.trim()) return;
    setEnviando(true);
    try {
      await axios.post(`${API}/coberturas/pipa/${pipa.id}/bitacora`, { nota: nota.trim() });
      setNota("");
      toast.success("Nota agregada");
      cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setEnviando(false); }
  };

  const hoy = new Date().toISOString().slice(0, 10);
  const diasEnTaller = (iso) => {
    if (!iso) return "—";
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return d <= 0 ? "Hoy" : `${d} día${d === 1 ? "" : "s"}`;
  };
  const iniciales = (n) => (n || "—").split(/\s+/).map(x => x[0]).slice(0, 2).join("").toUpperCase();

  if (!data) return null;
  const info = data.info || {};

  return (
    <div className="sup-modal-bg" onClick={onClose} data-testid="panel-taller-bg">
      <div className="sup-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 580 }} data-testid="panel-taller">
        <header className="sup-modal-head">
          <div>
            <div className="text-2xl font-bold">
              <i className="fa-solid fa-screwdriver-wrench" style={{ color: "#DC2626", marginRight: 6 }}/>
              Pipa #{info.id_pipa || pipa.id_pipa}
            </div>
            <div className="text-sm text-slate-500">En taller · bitácora de seguimiento</div>
          </div>
          <button onClick={onClose} aria-label="Cerrar" data-testid="panel-taller-cerrar"
                  style={{ background: "transparent", border: 0, fontSize: 22, cursor: "pointer", color: "#64748B" }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </header>

        {/* Sección INFO 2x2 */}
        <section style={{ padding: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }} data-testid="panel-taller-info">
            <InfoCell label="Motivo de entrada" value={info.taller_motivo || "No especificado"}/>
            <InfoCell label="Días en taller" value={diasEnTaller(info.taller_desde)}/>
            <InfoCell label="Pipa sustituta" value={info.zona_prestamo_destino || "Ninguna"}/>
            <InfoCell label="Reportada por" value={data.reportada_por?.actor_nombre || "—"}/>
          </div>
        </section>

        {/* Sección HISTORIAL DE ENTRADAS/SALIDAS CON FOTOS */}
        {historial.length > 0 && (
          <section style={{ padding: "0 12px 12px" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 800, color: "#0F172A" }}>
              <i className="fa-solid fa-clock-rotate-left" style={{ marginRight: 6 }}/>Historial de taller
            </h3>
            <div style={{ display: "grid", gap: 8 }}>
              {historial.map((h) => {
                const fotosE = h.fotos_entrada || [];
                const fotosS = h.fotos_salida || [];
                const abierto = !h.fecha_regreso_real;
                return (
                  <div key={h.id} style={{
                    background: abierto ? "#FEF3C7" : "#F8FAFC",
                    border: `1px solid ${abierto ? "#FDE68A" : "#E2E8F0"}`,
                    borderRadius: 10, padding: 10,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontWeight: 800, fontSize: 12, color: abierto ? "#92400E" : "#475569" }}>
                        {abierto
                          ? <><i className="fa-solid fa-wrench" style={{ marginRight: 4 }}/>EN TALLER</>
                          : <><i className="fa-solid fa-circle-check" style={{ marginRight: 4 }}/>SALIDA</>}
                      </span>
                      <span style={{ fontSize: 11, color: "#94A3B8" }}>
                        {(h.fecha_entrada || "").slice(0, 10)}
                        {h.fecha_regreso_real && <> → {h.fecha_regreso_real.slice(0, 10)}</>}
                      </span>
                    </div>
                    {h.motivo && <div style={{ fontSize: 12, color: "#334155", marginBottom: 4 }}><strong>Motivo:</strong> {h.motivo}</div>}
                    {h.lugar_taller && <div style={{ fontSize: 12, color: "#334155", marginBottom: 4 }}><strong>Lugar:</strong> {h.lugar_taller}</div>}
                    {h.registrado_por_nombre && <div style={{ fontSize: 11, color: "#64748B", marginBottom: 6 }}>Registrado por: {h.registrado_por_nombre}</div>}
                    {/* Fotos de entrada */}
                    {fotosE.length > 0 && (
                      <div style={{ marginBottom: fotosS.length ? 6 : 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#92400E", marginBottom: 4 }}>
                          <i className="fa-solid fa-camera" style={{ marginRight: 4 }}/>Fotos entrada ({fotosE.length})
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {fotosE.map((url, i) => (
                            <button key={i} onClick={() => setFotoModal({ url, fotos: fotosE, titulo: `Pipa #${h.pipa_numero} — entrada ${(h.fecha_entrada||"").slice(0,10)}` })}
                              style={{ border: "none", background: "none", padding: 0, cursor: "pointer" }}>
                              <img src={url} alt={`entrada-${i}`} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6, border: "2px solid #FCA5A5" }}/>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Fotos de salida */}
                    {fotosS.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#15803D", marginBottom: 4 }}>
                          <i className="fa-solid fa-camera" style={{ marginRight: 4 }}/>Fotos salida ({fotosS.length})
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {fotosS.map((url, i) => (
                            <button key={i} onClick={() => setFotoModal({ url, fotos: fotosS, titulo: `Pipa #${h.pipa_numero} — salida ${(h.fecha_regreso_real||"").slice(0,10)}` })}
                              style={{ border: "none", background: "none", padding: 0, cursor: "pointer" }}>
                              <img src={url} alt={`salida-${i}`} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6, border: "2px solid #86EFAC" }}/>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {fotosE.length === 0 && fotosS.length === 0 && (
                      <div style={{ fontSize: 11, color: "#94A3B8", fontStyle: "italic" }}>Sin fotos en este registro</div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Sección BITÁCORA */}
        <section style={{ padding: "0 12px 12px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: "#0F172A" }}>
              <i className="fa-solid fa-book"/> Bitácora
            </h3>
            <span style={{ fontSize: 12, color: "#64748B", fontWeight: 700 }} data-testid="panel-taller-conteo">
              {data.total} {data.total === 1 ? "nota" : "notas"}
            </span>
          </div>
          {data.items.length === 0 ? (
            <div style={{ textAlign: "center", padding: 24, color: "#94A3B8", fontSize: 13, background: "#F8FAFC", borderRadius: 8 }} data-testid="panel-taller-vacia">
              Sin notas todavía. Escribe la primera abajo.
            </div>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8, maxHeight: 280, overflow: "auto" }} data-testid="panel-taller-lista">
              {data.items.map((n) => {
                const esHoy = n.fecha === hoy;
                return (
                  <li key={n.id} style={{
                    background: esHoy ? "#FEE2E2" : "#F8FAFC",
                    border: `1px solid ${esHoy ? "#FCA5A5" : "#E2E8F0"}`,
                    borderRadius: 10, padding: 10, display: "grid", gridTemplateColumns: "32px 1fr", gap: 10
                  }} data-testid={`nota-${n.id}`}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1D4ED8", color: "white", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 12 }}>
                      {iniciales(n.supervisor_nombre)}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 4 }}>
                        <strong style={{ color: "#0F172A" }}>{n.supervisor_nombre}</strong>
                        {" · "}{new Date(n.creado_en).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
                      </div>
                      <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>{n.nota}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Sección AGREGAR NOTA */}
        {puedeEscribir && (
          <section style={{ padding: "0 12px 12px" }} data-testid="panel-taller-form">
            <textarea
              rows={3}
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="¿Qué pasó hoy con esta pipa?"
              data-testid="panel-taller-textarea"
              style={{ width: "100%", border: "1px solid #CBD5E1", borderRadius: 8, padding: 10, fontSize: 13, resize: "vertical" }}
            />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6 }}>
              <span style={{ fontSize: 11, color: "#64748B" }}>
                <i className="fa-solid fa-circle-info"/> Todos los supervisores de pipas verán esta nota.
              </span>
              <button
                data-testid="panel-taller-enviar"
                disabled={enviando || !nota.trim()}
                onClick={enviar}
                style={{ background: "#1D4ED8", color: "white", border: 0, borderRadius: 8, padding: "8px 14px", fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <i className="fa-solid fa-paper-plane"/> {enviando ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </section>
        )}

        {/* Botón quitar de taller + fotos de salida */}
        {puedeQuitar && (
          <section style={{ padding: 12, borderTop: "1px solid #E2E8F0" }}>
            <div style={{ marginBottom: 10 }}>
              <PhotoPicker
                fotos={fotosSalida}
                setFotos={setFotosSalida}
                max={5}
                label="FOTOS DE SALIDA (opcional)"
                testIdPrefix="taller-salida-foto"
              />
            </div>
            <button
              className="sup-pipa-action-taller"
              onClick={() => onQuitarTaller(fotosSalida.map((f) => f.dataURL))}
              data-testid="panel-taller-quitar"
              style={{ width: "100%", justifyContent: "center", padding: 12 }}
            >
              <i className="fa-solid fa-screwdriver-wrench"/> Pipa lista — quitar de taller
            </button>
          </section>
        )}
      </div>

      {/* ── Modal Foto — mismo patrón que TabActividadChoferes ── */}
      {fotoModal && (
        <div onClick={() => setFotoModal(null)}
          style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.85)",
                   display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: "90vw", maxHeight: "90vh", cursor: "default" }}>
            <img src={fotoModal.url} alt="evidencia ampliada"
                 style={{ maxWidth: "100%", maxHeight: "72vh", borderRadius: 8, display: "block" }}/>
            <div className="flex items-center justify-between mt-3 text-white text-xs">
              <div className="font-bold opacity-90">{fotoModal.titulo}</div>
              <div className="flex gap-2">
                <a href={fotoModal.url} target="_blank" rel="noopener noreferrer"
                   style={{ background: "rgba(255,255,255,0.2)", color: "white", padding: "4px 12px", borderRadius: 6, textDecoration: "none", fontWeight: 700, fontSize: 12 }}>
                  <i className="fa-solid fa-up-right-from-square mr-1"/> Abrir en nueva pestaña
                </a>
                <button onClick={() => setFotoModal(null)}
                  style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "none", padding: "4px 12px", borderRadius: 6, fontWeight: 700, cursor: "pointer", fontSize: 12 }}>
                  <i className="fa-solid fa-xmark"/> Cerrar
                </button>
              </div>
            </div>
            {fotoModal.fotos.length > 1 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {fotoModal.fotos.map((u, i) => (
                  <button key={i} onClick={() => setFotoModal(p => ({ ...p, url: u }))}
                    style={{ border: "none", background: "none", padding: 0, cursor: "pointer" }}>
                    <img src={u} alt={`f${i}`}
                         style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 4,
                                  border: u === fotoModal.url ? "2px solid #C9A84C" : "2px solid transparent" }}/>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Resumen por taller ─────────────────────────────────────────────────────
function BtnResumenTaller() {
  const [abierto, setAbierto] = useState(false);
  const [grupos, setGrupos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [exportando, setExportando] = useState(false);

  const verResumen = async () => {
    setCargando(true);
    setAbierto(true);
    try {
      const r = await axios.get(`${API}/coberturas/pipas-taller/resumen`);
      setGrupos(r.data.grupos || []);
    } catch {
      toast.error("Error al cargar resumen");
      setAbierto(false);
    } finally {
      setCargando(false);
    }
  };

  const exportar = async () => {
    setExportando(true);
    try {
      const r = await axios.get(`${API}/coberturas/pipas-taller/resumen/exportar`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = `taller_resumen_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      toast.success("Excel descargado");
    } catch {
      toast.error("Error al exportar");
    } finally {
      setExportando(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={verResumen}
        data-testid="btn-resumen-taller"
        style={{ background: "#7C3AED", color: "white", border: 0, padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
      >
        <i className="fa-solid fa-chart-bar mr-1"/>Resumen taller
      </button>

      {abierto && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
             onClick={() => setAbierto(false)}>
          <div style={{ background: "white", borderRadius: 16, width: "100%", maxWidth: 560, maxHeight: "80vh", overflowY: "auto", padding: 20 }}
               onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <span style={{ fontSize: 16, fontWeight: 800 }}><i className="fa-solid fa-screwdriver-wrench mr-2" style={{ color: "#7C3AED" }}/>Resumen por taller</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={exportar} disabled={exportando}
                        data-testid="btn-exportar-taller"
                        style={{ background: "#15803D", color: "white", border: 0, padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  <i className="fa-solid fa-file-excel mr-1"/>{exportando ? "…" : "Exportar Excel"}
                </button>
                <button type="button" onClick={() => setAbierto(false)}
                        style={{ background: "#F1F5F9", color: "#475569", border: 0, padding: "6px 10px", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>
                  <i className="fa-solid fa-xmark"/>
                </button>
              </div>
            </div>
            {cargando && <div style={{ textAlign: "center", padding: 24, color: "#94A3B8" }}>Cargando…</div>}
            {!cargando && grupos && grupos.length === 0 && (
              <div style={{ textAlign: "center", padding: 24, color: "#94A3B8" }}>Sin registros de taller</div>
            )}
            {!cargando && grupos && grupos.map((g) => (
              <div key={g.lugar_taller} style={{ border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, marginBottom: 10 }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: "#0F172A", marginBottom: 6 }}>{g.lugar_taller}</div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 13, color: "#475569", marginBottom: 8 }}>
                  <span><i className="fa-solid fa-truck-droplet mr-1"/>{g.total_pipas_distintas} pipa(s)</span>
                  <span><i className="fa-solid fa-arrow-right-to-bracket mr-1"/>{g.total_entradas} entrada(s)</span>
                  <span><i className="fa-solid fa-clock mr-1"/>{g.dias_acumulados} días acumulados</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {g.motivos.map((m) => (
                    <span key={m.motivo} style={{ background: "#F1F5F9", borderRadius: 20, padding: "2px 10px", fontSize: 11, color: "#334155" }}>
                      {m.motivo} ({m.count})
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   TarjetaPipaSupervisor — Tarjeta de pipa para admin/supervisor_pipas/supervisor_general_pipas.
   Diseño unificado con TarjetaVehiculoCT: punto de color, zona/chofer como subtítulo,
   botón de link, y acciones de taller integradas en la tarjeta.
   ═══════════════════════════════════════════════════════════ */
function TarjetaPipaSupervisor({ pipa: p, intervencion: i, esTaller, esPrestada,
                                  puedeAccionarTaller, onVerDetalle, onVerTaller,
                                  onEntrarTaller, onSalirTaller }) {
  // ── Punto de color + texto de estatus ──
  let dotColor, statusText, statusColor;
  if (esTaller) {
    dotColor    = "#D97706";   // ámbar
    statusText  = "En taller";
    statusColor = "#92400E";
  } else if (esPrestada) {
    dotColor    = "#0EA5E9";   // azul
    statusText  = "Prestada";
    statusColor = "#1E40AF";
  } else if (!i) {
    dotColor    = "#94A3B8";   // gris
    statusText  = "Sin actividad hoy";
    statusColor = "#64748B";
  } else {
    dotColor    = "#16A34A";   // verde
    statusText  = i.estado === "EN_CURSO"
      ? `En ruta · ${i.cobertura_pct || 0}%`
      : `${i.estado} · ${i.cobertura_pct || 0}%`;
    statusColor = "#14532D";
  }
  const sinActividad = !esTaller && !esPrestada && !i;

  const copiarLink = async (e) => {
    e.stopPropagation();
    const pid = p.id || p.id_pipa;
    const url = `${window.location.origin}/historial-unidad/${pid}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado al portapapeles ✓");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.cssText = "position:fixed;opacity:0;top:0;left:0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Link copiado al portapapeles ✓");
      } catch { toast.error("No se pudo copiar"); }
    }
  };

  return (
    <div
      data-testid={`pipa-card-${p.id_pipa}`}
      style={{
        background: "#FFFFFF",
        border: `2px solid ${esTaller ? "#FCD34D" : esPrestada ? "#BFDBFE" : sinActividad ? "#E2E8F0" : "#D1FAE5"}`,
        borderRadius: 14,
        padding: "14px 14px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        cursor: "default",
        minHeight: 130,
        opacity: sinActividad ? 0.75 : 1,
        transition: "opacity 0.2s",
      }}
    >
      {/* Fila superior: número + botón link */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 800, fontSize: 20,
                       color: "#0F172A", letterSpacing: "-0.5px", lineHeight: 1 }}>
          #{p.id_pipa}
        </span>
        <button
          type="button"
          title="Copiar link del historial"
          data-testid={`sup-btn-link-${p.id_pipa}`}
          onClick={copiarLink}
          style={{
            border: "none", borderRadius: "50%",
            background: "#16A34A", color: "#FFFFFF",
            cursor: "pointer", width: 28, height: 28, padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, flexShrink: 0,
            boxShadow: "0 1px 4px rgba(22,163,74,0.35)",
          }}
        >
          <i className="fa-solid fa-link"/>
        </button>
      </div>

      {/* Punto de color + texto estatus */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{
          width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
          background: dotColor, boxShadow: `0 0 0 2px ${dotColor}33`,
        }}/>
        <span style={{ fontSize: 12, fontWeight: 700, color: statusColor }}>{statusText}</span>
      </div>

      {/* Subtítulo: zona y chofer */}
      <div style={{ fontSize: 12, color: "#94A3B8", lineHeight: 1.4 }}>
        {p.zona_asignada || "—"}
        {" · "}
        {p.chofer_titular_nombre || p.chofer_nombre || "Sin titular"}
      </div>

      {/* Acciones — empujar al fondo */}
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
        {esTaller ? (
          <>
            <button
              type="button"
              data-testid={`sup-btn-salida-${p.id_pipa}`}
              onClick={(e) => { e.stopPropagation(); onSalirTaller(); }}
              style={{
                width: "100%", border: 0, cursor: "pointer", borderRadius: 8,
                padding: "9px 0", fontSize: 13, fontWeight: 800,
                background: "#16A34A", color: "#FFFFFF",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                boxShadow: "0 2px 8px rgba(22,163,74,0.3)",
              }}
            >
              <i className="fa-solid fa-circle-check"/>
              Marcar salida de taller
            </button>
            <button
              type="button"
              data-testid={`sup-btn-bitacora-${p.id_pipa}`}
              onClick={(e) => { e.stopPropagation(); onVerTaller(); }}
              style={{
                width: "100%", border: "1px solid #FCD34D", cursor: "pointer", borderRadius: 8,
                padding: "6px 0", fontSize: 12, fontWeight: 700,
                background: "#FFFBEB", color: "#92400E",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              <i className="fa-solid fa-book"/>
              Ver bitácora
            </button>
          </>
        ) : puedeAccionarTaller && !esPrestada ? (
          <button
            type="button"
            data-testid={`sup-btn-entrar-taller-${p.id_pipa}`}
            onClick={(e) => { e.stopPropagation(); onEntrarTaller(e); }}
            style={{
              width: "100%", border: "1px solid #CBD5E1", cursor: "pointer", borderRadius: 8,
              padding: "7px 0", fontSize: 12, fontWeight: 700,
              background: "#F8FAFC", color: "#475569",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <i className="fa-solid fa-right-to-bracket"/>
            Registrar en taller
          </button>
        ) : (
          /* Click para ver detalle — mantiene comportamiento anterior */
          <button
            type="button"
            data-testid={`sup-btn-detalle-${p.id_pipa}`}
            onClick={(e) => { e.stopPropagation(); onVerDetalle(); }}
            style={{
              width: "100%", border: "1px solid #E2E8F0", cursor: "pointer", borderRadius: 8,
              padding: "7px 0", fontSize: 12, fontWeight: 600,
              background: "#F8FAFC", color: "#64748B",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <i className="fa-solid fa-eye"/>
            Ver detalle
          </button>
        )}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   TarjetaVehiculoCT — Tarjeta de unidad para rol control_taller.
   Diseño genérico y escalable (futuro: otros tipos de vehículo además de pipas).
   No muestra zona, turno ni chofer — solo número de unidad, estado y acción de taller.
   ═══════════════════════════════════════════════════════════ */
function TarjetaVehiculoCT({ pipa: p, esTaller, esPrestada, onVerTaller, onEntrarTaller, onSalirTaller }) {
  /* ── Etiqueta de tipo de vehículo (escalable a futuro) ── */
  const tipoVehiculo = p.tipo_vehiculo || "Pipa";

  /* ── Estado */
  let estadoLabel, estadoColor, estadoBg, estadoIcon;
  if (esTaller) {
    estadoLabel = "EN TALLER";
    estadoColor = "#92400E";
    estadoBg    = "#FEF3C7";
    estadoIcon  = "fa-screwdriver-wrench";
  } else if (esPrestada) {
    estadoLabel = "PRESTADA";
    estadoColor = "#1E40AF";
    estadoBg    = "#EFF6FF";
    estadoIcon  = "fa-arrow-right-arrow-left";
  } else {
    estadoLabel = "OPERATIVA";
    estadoColor = "#14532D";
    estadoBg    = "#F0FDF4";
    estadoIcon  = "fa-circle-check";
  }

  return (
    <div
      data-testid={`ct-card-${p.id_pipa}`}
      style={{
        background: "#FFFFFF",
        border: `2px solid ${esTaller ? "#FCD34D" : esPrestada ? "#BFDBFE" : "#D1FAE5"}`,
        borderRadius: 14,
        padding: "14px 14px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        cursor: "default",
        minHeight: 120,
      }}
    >
      {/* Número de unidad + tipo + botón copiar link */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <span style={{
          fontVariantNumeric: "tabular-nums",
          fontWeight: 800,
          fontSize: 20,
          color: "#0F172A",
          letterSpacing: "-0.5px",
          lineHeight: 1,
        }}>
          #{p.id_pipa}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {tipoVehiculo !== "Pipa" && (
            <span style={{
              fontSize: 10, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: "0.06em", padding: "2px 8px", borderRadius: 20,
              background: "#F1F5F9", color: "#64748B",
            }}>
              {tipoVehiculo}
            </span>
          )}
          {/* Botón copiar link — ícono circular verde */}
          <button
            type="button"
            title="Copiar link del historial de esta unidad"
            data-testid={`ct-btn-link-${p.id_pipa}`}
            onClick={async (e) => {
              e.stopPropagation();
              // p.id es UUID (no siempre existe); fallback a id_pipa numérico
              const pid = p.id || p.id_pipa;
              const url = `${window.location.origin}/historial-unidad/${pid}`;
              try {
                await navigator.clipboard.writeText(url);
                toast.success("Link copiado al portapapeles ✓");
              } catch {
                // Fallback para navegadores sin Clipboard API (móviles, foco perdido)
                try {
                  const ta = document.createElement("textarea");
                  ta.value = url;
                  ta.style.cssText = "position:fixed;opacity:0;top:0;left:0";
                  document.body.appendChild(ta);
                  ta.select();
                  document.execCommand("copy");
                  document.body.removeChild(ta);
                  toast.success("Link copiado al portapapeles ✓");
                } catch {
                  toast.error("No se pudo copiar. Copia manualmente: " + url);
                }
              }
            }}
            style={{
              border: "none", borderRadius: "50%",
              background: "#16A34A", color: "#FFFFFF",
              cursor: "pointer",
              width: 28, height: 28, padding: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, flexShrink: 0,
              boxShadow: "0 1px 4px rgba(22,163,74,0.35)",
              transition: "background 0.2s",
            }}
          >
            <i className="fa-solid fa-link"/>
          </button>
        </div>
      </div>

      {/* Badge de estado */}
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        alignSelf: "flex-start",
        fontSize: 11, fontWeight: 700, padding: "3px 10px",
        borderRadius: 20, background: estadoBg, color: estadoColor,
        letterSpacing: "0.04em",
      }}>
        <i className={`fa-solid ${estadoIcon}`} style={{ fontSize: 10 }}/>
        {estadoLabel}
      </span>

      {/* Acción */}
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
        {esTaller ? (
          <>
            {/* Botón principal: SALIDA — verde, prominente */}
            <button
              type="button"
              data-testid={`ct-btn-salida-${p.id_pipa}`}
              onClick={(e) => { e.stopPropagation(); onSalirTaller(); }}
              style={{
                width: "100%", border: 0, cursor: "pointer", borderRadius: 8,
                padding: "10px 0", fontSize: 13, fontWeight: 800,
                background: "#16A34A", color: "#FFFFFF",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                boxShadow: "0 2px 8px rgba(22,163,74,0.3)",
              }}
            >
              <i className="fa-solid fa-circle-check"/>
              Marcar salida de taller
            </button>
            {/* Botón secundario: bitácora — más discreto */}
            <button
              type="button"
              data-testid={`ct-btn-taller-${p.id_pipa}`}
              onClick={(e) => { e.stopPropagation(); onVerTaller(); }}
              style={{
                width: "100%", border: "1px solid #FCD34D", cursor: "pointer", borderRadius: 8,
                padding: "7px 0", fontSize: 12, fontWeight: 700,
                background: "#FFFBEB", color: "#92400E",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              <i className="fa-solid fa-book"/>
              Ver bitácora
            </button>
          </>
        ) : !esPrestada ? (
          <button
            type="button"
            data-testid={`ct-btn-entrar-${p.id_pipa}`}
            onClick={(e) => { e.stopPropagation(); onEntrarTaller(e); }}
            style={{
              width: "100%", border: 0, cursor: "pointer", borderRadius: 8,
              padding: "8px 0", fontSize: 12, fontWeight: 700,
              background: "#F8FAFC", color: "#475569",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <i className="fa-solid fa-right-to-bracket"/>
            Registrar en taller
          </button>
        ) : (
          <div style={{ fontSize: 11, color: "#94A3B8", textAlign: "center", padding: "6px 0" }}>
            Unidad prestada
          </div>
        )}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   ModalSalidaTaller — Salida rápida desde la tarjeta (sin pasar por bitácora).
   Genérico: usa "unidad" en lugar de "pipa" para soportar futura flotilla completa.
   ═══════════════════════════════════════════════════════════ */
function ModalSalidaTaller({ pipa: p, onClose, onConfirmado }) {
  const [fotos, setFotos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const tipoUnidad = p.tipo_vehiculo || "Unidad";
  const label = `${tipoUnidad} #${p.id_pipa}`;

  const confirmar = async () => {
    setEnviando(true);
    try {
      await onConfirmado(fotos.map((f) => f.dataURL));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="sup-modal-bg" onClick={onClose} data-testid="modal-salida-taller-bg">
      <div className="sup-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}
           data-testid="modal-salida-taller">
        <header className="sup-modal-head">
          <div>
            <div className="text-2xl font-bold" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ background: "#16A34A", color: "#fff", borderRadius: "50%", width: 34, height: 34,
                             display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                <i className="fa-solid fa-circle-check"/>
              </span>
              {label} — Salida de taller
            </div>
            <div className="text-sm text-slate-500" style={{ marginTop: 2 }}>
              Confirma que la unidad ya salió y está operativa
            </div>
          </div>
          <button onClick={onClose} aria-label="Cerrar"
                  style={{ background: "transparent", border: 0, fontSize: 22, cursor: "pointer", color: "#64748B" }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </header>

        <section style={{ padding: "0 16px 16px" }}>
          {/* Fotos de salida opcionales */}
          <div style={{ marginBottom: 16 }}>
            <PhotoPicker
              fotos={fotos}
              setFotos={setFotos}
              max={5}
              label="FOTOS DE SALIDA (opcional)"
              testIdPrefix="salida-rapida-foto"
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose}
              style={{ flex: 1, border: "1px solid #CBD5E1", background: "#F8FAFC", color: "#475569",
                       borderRadius: 8, padding: "10px 0", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
              Cancelar
            </button>
            <button onClick={confirmar} disabled={enviando} data-testid="modal-salida-confirmar"
              style={{ flex: 2, background: "#16A34A", color: "white", border: 0,
                       borderRadius: 8, padding: "10px 0", fontWeight: 800, cursor: "pointer", fontSize: 13,
                       display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                       opacity: enviando ? 0.7 : 1 }}>
              <i className="fa-solid fa-circle-check"/>
              {enviando ? "Registrando…" : `Confirmar salida de ${tipoUnidad.toLowerCase()}`}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function ModalEntrarTaller({ pipa, onClose, onConfirmado }) {
  const [motivo, setMotivo] = useState("");
  const [lugarTaller, setLugarTaller] = useState("");
  const [fotos, setFotos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const hoyISO = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const [fechaEntrada, setFechaEntrada] = useState(hoyISO);

  const confirmar = async () => {
    setEnviando(true);
    try {
      // Si la fecha es hoy → omitir (servidor usa ahora() exacto).
      // Si es una fecha anterior → enviar al mediodía para evitar ambigüedad de zona horaria.
      const fechaManual = fechaEntrada !== hoyISO ? `${fechaEntrada}T12:00:00` : undefined;
      const payload = {
        motivo: motivo || null,
        lugar_taller: lugarTaller.trim() || null,
        fotos: fotos.map((f) => f.dataURL),
        ...(fechaManual ? { fecha_entrada_manual: fechaManual } : {}),
      };
      if (pipa._isCatalog) {
        await axios.put(`${API}/vehiculos/${pipa.no_economico}/entrar-taller`, payload);
        toast.success(`Unidad #${pipa.no_economico} marcada como EN TALLER`);
      } else {
        await axios.put(`${API}/coberturas/pipa/${pipa.id || pipa.id_pipa}/entrar-taller`, payload);
        toast.success(`Pipa #${pipa.id_pipa} marcada como EN TALLER`);
      }
      onConfirmado();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al marcar en taller");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={onClose}>
      <div
        style={{ background: "#fff", borderRadius: "16px 16px 0 0", width: "100%", maxWidth: 480, maxHeight: "88vh", overflowY: "auto", padding: "1.1rem 1.1rem 1.6rem" }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <i className="fa-solid fa-screwdriver-wrench" style={{ color: "#666" }} />
          <span style={{ fontSize: 15, fontWeight: 600 }}>Registrar {pipa._isCatalog ? "unidad" : "pipa"} en taller</span>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>{pipa._isCatalog ? "Unidad" : "Pipa"}</label>
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f5f5f5", border: "1px solid #e5e5e5", borderRadius: 8, padding: "8px 12px", fontSize: 13 }}>
            {pipa._isCatalog ? (
              <>
                <span style={{ background: "#e0f2fe", color: "#0369a1", fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>
                  {pipa.departamento || "—"}
                </span>
                <span style={{ fontWeight: 600 }}>#{pipa.no_economico}</span>
                {pipa.tipo && <span style={{ color: "#999", fontSize: 12, marginLeft: "auto" }}>{pipa.tipo}</span>}
              </>
            ) : (
              <>
                <span style={{ background: "#fde8e8", color: "#c0392b", fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>
                  {pipa.zona_asignada || "—"}
                </span>
                <span style={{ fontWeight: 600 }}>Pipa #{pipa.id_pipa}</span>
                {pipa.chofer_titular_nombre && (
                  <span style={{ color: "#999", fontSize: 12, marginLeft: "auto" }}>{pipa.chofer_titular_nombre}</span>
                )}
              </>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, color: "#333", fontWeight: 700, display: "block", marginBottom: 5 }}>Motivo *</label>
          <input
            type="text"
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            placeholder="Ej. Falla mecánica, llanta ponchada, mantenimiento…"
            style={{ width: "100%", boxSizing: "border-box", border: "2px solid #cbd5e1", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontWeight: 500 }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, color: "#333", fontWeight: 700, display: "block", marginBottom: 5 }}>Lugar o taller</label>
          <input
            type="text"
            value={lugarTaller}
            onChange={e => setLugarTaller(e.target.value)}
            placeholder="Ej. Taller municipal, Mecánica Ramírez…"
            style={{ width: "100%", boxSizing: "border-box", border: "2px solid #cbd5e1", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontWeight: 500 }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, color: "#333", fontWeight: 700, display: "block", marginBottom: 5 }}>
            Fecha de entrada
          </label>
          <input
            type="date"
            value={fechaEntrada}
            max={hoyISO}
            onChange={e => setFechaEntrada(e.target.value || hoyISO)}
            data-testid="taller-fecha-entrada"
            style={{ width: "100%", boxSizing: "border-box", border: "2px solid #cbd5e1", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontWeight: 500 }}
          />
          {fechaEntrada !== hoyISO && (
            <div style={{ fontSize: 11, color: "#D97706", marginTop: 4 }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 4 }}/>
              Captura retroactiva — se registrará como {fechaEntrada}
            </div>
          )}
        </div>

        <div style={{ marginTop: 4 }}>
          <PhotoPicker fotos={fotos} setFotos={setFotos} max={5} label="FOTOS DE ENTRADA" testIdPrefix="taller-entrada-foto" />
        </div>

        <button
          onClick={confirmar}
          disabled={enviando}
          style={{
            width: "100%", marginTop: 14, background: "#111", color: "#fff", border: "none",
            borderRadius: 8, padding: 12, fontSize: 14, fontWeight: 600, cursor: enviando ? "default" : "pointer",
            opacity: enviando ? 0.6 : 1,
          }}
        >
          {enviando ? "Guardando…" : "Confirmar pipa en taller"}
        </button>
      </div>
    </div>
  );
}

function InfoCell({ label, value }) {
  return (
    <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: 10 }}>
      <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div style={{ fontSize: 14, color: "#0F172A", fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   TabAsignacionDia — rediseño 14-jun-2026 (Octavio)
   Filas: chofer + pipa + ZONA BASE + lista de parques del día
   (con agregar/quitar excepciones). Botón "Guardar permanente"
   por fila para promover excepciones a `ruta_puntos`.
   ═══════════════════════════════════════════════════════════ */
function TabAsignacionDia({ user }) {
  const hoyISO = new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(hoyISO);
  const esHoy = fecha === hoyISO;
  const puedeEditar = ["admin", "supervisor_general_pipas", "supervisor_pipas"].includes(user?.rol) && esHoy;

  // Datos de referencia
  const [choferesCatalogo, setChoferesCatalogo] = useState([]);
  const [pipasCatalogo, setPipasCatalogo]       = useState([]);
  const [zonasCatalogo, setZonasCatalogo]       = useState([]);
  const [titulares, setTitulares]               = useState({}); // chofer_id -> {pipa_titular, zona_titular, nombre}

  // Filas en edición — cada fila tiene:
  //   { _k, chofer_id, pipa_id, zona_dia,
  //     parques_base[], removidos_set, agregados[], picker_open, picker_query }
  const [filas, setFilas] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [permanenteGuardando, setPermanenteGuardando] = useState({}); // idx -> bool
  const [filaExpandida, setFilaExpandida] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [chDia, pipas, zonas, usuarios] = await Promise.all([
        axios.get(`${API}/coberturas/choferes-pipa-dia`, { params: { fecha } }),
        axios.get(`${API}/pipas`),
        axios.get(`${API}/zonas`),
        axios.get(`${API}/usuarios`, { params: { rol: "chofer" } }).catch(() => ({ data: [] })),
      ]);

      const catalogo = (usuarios.data || [])
        .filter((u) => u.activo !== false)
        .map((u) => ({ id: u.id, nombre: u.nombre, n_empleado: u.n_empleado }))
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
      setChoferesCatalogo(catalogo);

      const pipasList = (pipas.data || [])
        .map((p) => ({
          id_pipa: p.id_pipa,
          zona: p.zona_asignada || (p.zonas || [])[0] || null,
          estado: (p.estado_operativo || p.estado || "OPERATIVA").toUpperCase(),
        }))
        .sort((a, b) => String(a.id_pipa).localeCompare(String(b.id_pipa)));
      setPipasCatalogo(pipasList);

      const ZONAS_PERMITIDAS = new Set(Array.from({ length: 14 }, (_, i) => `Z${i + 1}`));
      const zonasList = (zonas.data || [])
        .map((z) => z.clave || z.zona_id || z.id || `Z${z.numero}`)
        .filter((z) => ZONAS_PERMITIDAS.has(z));
      setZonasCatalogo(zonasList.length ? zonasList : Array.from({ length: 14 }, (_, i) => `Z${i + 1}`));

      const titMap = {};
      const filasIni = [];
      const pipasEnTallerSet = new Set(pipasList.filter(p => p.estado === "EN_TALLER" || p.estado === "TALLER").map(p => p.id_pipa));
      (chDia.data.items || []).forEach((it) => {
        titMap[it.chofer_id] = {
          nombre: it.nombre,
          pipa_titular: it.pipa_titular,
          zona_titular: it.zona_titular,
        };
        // Si la pipa sugerida está en taller, no la pre-seleccionamos.
        let pipaInicial = it.pipa_hoy || it.pipa_titular || "";
        if (pipaInicial && pipasEnTallerSet.has(pipaInicial)) pipaInicial = "";
        filasIni.push({
          _k: it.chofer_id,
          chofer_id: it.chofer_id,
          pipa_id: pipaInicial,
          zona_dia: it.zona_titular || "",
          parques_base: [],
          parques_loaded: false,
          removidos_set: new Set(),
          agregados: [],     // array de {clave_ruta, nombre, ...}
          picker_open: false,
          picker_query: "",
          picker_results: [],
        });
      });
      setTitulares(titMap);
      setFilas(filasIni);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando datos");
    }
  }, [fecha]);

  useEffect(() => { cargar(); }, [cargar]);

  // Carga parques base + excepciones de una fila cuando se expande / cambia zona
  const cargarParques = useCallback(async (idx, chofer_id, zona) => {
    if (!chofer_id || !zona) return;
    try {
      const r = await axios.get(`${API}/coberturas/parques-del-dia`, {
        params: { fecha, zona, chofer_id },
      });
      const d = r.data || {};
      setFilas((arr) => arr.map((f, i) => i === idx ? {
        ...f,
        parques_base: d.base || [],
        parques_loaded: true,
        removidos_set: new Set(d.removidos_ids || []),
        agregados: d.agregados || [],
      } : f));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando parques del día");
    }
  }, [fecha]);

  const pipasAsignadas = useMemo(() => {
    const m = {};
    (filas || []).forEach((f) => { if (f.pipa_id) m[f.pipa_id] = (m[f.pipa_id] || 0) + 1; });
    return m;
  }, [filas]);

  const cambiosDetectados = useMemo(() => {
    if (!filas) return [];
    return filas.filter((f) => {
      const tit = titulares[f.chofer_id] || {};
      const cambioPipa = f.pipa_id && f.pipa_id !== tit.pipa_titular;
      const cambioZona = f.zona_dia && f.zona_dia !== tit.zona_titular;
      const cambioParques = (f.removidos_set && f.removidos_set.size > 0) || (f.agregados && f.agregados.length > 0);
      return cambioPipa || cambioZona || cambioParques;
    });
  }, [filas, titulares]);

  const setFila = (idx, patch) => {
    setFilas((arr) => arr.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const eliminarFila = (idx) => {
    if (!puedeEditar) return;
    setFilas((arr) => arr.filter((_, i) => i !== idx));
    if (filaExpandida === idx) setFilaExpandida(null);
  };
  const agregarFila = () => {
    if (!puedeEditar) return;
    setFilas((arr) => [...arr, {
      _k: `new-${Date.now()}`, chofer_id: "", pipa_id: "",
      zona_dia: "", parques_base: [], parques_loaded: false,
      removidos_set: new Set(), agregados: [],
      picker_open: false, picker_query: "", picker_results: [],
    }]);
  };

  const toggleRemovido = (idx, clave_ruta) => {
    if (!puedeEditar) return;
    setFilas((arr) => arr.map((f, i) => {
      if (i !== idx) return f;
      const s = new Set(f.removidos_set);
      if (s.has(clave_ruta)) s.delete(clave_ruta); else s.add(clave_ruta);
      return { ...f, removidos_set: s };
    }));
  };

  const quitarAgregado = (idx, clave_ruta) => {
    if (!puedeEditar) return;
    setFilas((arr) => arr.map((f, i) => i !== idx ? f : {
      ...f, agregados: (f.agregados || []).filter((p) => p.clave_ruta !== clave_ruta)
    }));
  };

  const pickerDebounceRef = React.useRef({});
  const buscarParques = (idx, q) => {
    setFila(idx, { picker_query: q });
    if (pickerDebounceRef.current[idx]) {
      clearTimeout(pickerDebounceRef.current[idx]);
    }
    if (!q || q.trim().length < 2) {
      setFila(idx, { picker_results: [] });
      return;
    }
    pickerDebounceRef.current[idx] = setTimeout(async () => {
      try {
        const fila = filas[idx];
        const r = await axios.get(`${API}/coberturas/catalogo-parques`, {
          params: { q: q.trim(), zona: fila?.zona_dia || undefined, limite: 30 },
        });
        setFila(idx, { picker_results: r.data?.items || [] });
      } catch (e) {
        setFila(idx, { picker_results: [] });
      }
    }, 280);
  };

  const agregarParque = (idx, esp) => {
    if (!puedeEditar) return;
    setFilas((arr) => arr.map((f, i) => {
      if (i !== idx) return f;
      // Evitar duplicar contra base o agregados existentes
      const yaBase = (f.parques_base || []).some((p) => p.clave_ruta === esp.id_oficial);
      const yaAdd = (f.agregados || []).some((p) => p.clave_ruta === esp.id_oficial);
      if (yaBase || yaAdd) return f;
      return {
        ...f,
        agregados: [...(f.agregados || []), {
          clave_ruta: esp.id_oficial,
          nombre: esp.nombre,
          categoria: esp.categoria || esp.tipo_espacio,
          colonia: esp.colonia,
          gps_lat: esp.gps_lat,
          gps_lon: esp.gps_lon,
          es_excepcion: true,
        }],
        picker_query: "",
        picker_results: [],
        picker_open: false,
      };
    }));
  };

  const guardar = async () => {
    if (!puedeEditar) return;
    const sinChofer = filas.findIndex((f) => !f.chofer_id);
    if (sinChofer >= 0) return toast.error(`La fila ${sinChofer + 1} no tiene chofer seleccionado`);
    const repetidos = filas.map((f) => f.chofer_id).filter((v, i, a) => v && a.indexOf(v) !== i);
    if (repetidos.length) return toast.error("Hay choferes repetidos");
    const conflictoPipa = Object.entries(pipasAsignadas).find(([, n]) => n > 1);
    if (conflictoPipa) return toast.error(`La pipa #${conflictoPipa[0]} está asignada a más de un chofer`);

    setGuardando(true);
    try {
      const payload = {
        fecha,
        asignaciones: filas.map((f) => ({
          chofer_id: f.chofer_id,
          pipa_id: f.pipa_id || null,
          zonas: f.zona_dia ? [f.zona_dia] : [],
          zona_dia: f.zona_dia || null,
          remover_punto_ids: Array.from(f.removidos_set || []),
          agregar_punto_ids: (f.agregados || []).map((p) => p.clave_ruta).filter(Boolean),
        })),
      };
      const r = await axios.post(`${API}/coberturas/asignacion-dia/bulk`, payload);
      const d = r.data || {};
      toast.success(`Asignación guardada: ${d.guardados || 0} filas · ${d.cambios || 0} cambios · ${d.notificaciones || 0} aviso(s)`);
      if ((d.errores || []).length) {
        toast.error(`Con ${d.errores.length} advertencia(s). Revisa consola.`);
        console.warn("Bulk errores:", d.errores);
      }
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  const guardarPermanente = async (idx) => {
    if (!puedeEditar) return;
    const f = filas[idx];
    if (!f.chofer_id || !f.zona_dia) return toast.error("Falta chofer o zona");
    if (!f.removidos_set?.size && !f.agregados?.length) {
      return toast.error("No hay excepciones que guardar. Marca/agrega parques primero.");
    }
    if (!window.confirm(
      `¿Guardar permanentemente?\n\nSe modificará la ruta maestra de la zona ${f.zona_dia} para ` +
      `el día actual:\n+${f.agregados.length} agregado(s)\n−${f.removidos_set.size} removido(s)\n\n` +
      `Primero se guardará la asignación del día.`
    )) return;
    setPermanenteGuardando((m) => ({ ...m, [idx]: true }));
    try {
      // 1) Guardar bulk para persistir excepciones del día (si no se hizo aún)
      await guardar();
      // 2) Promover a permanente
      const r = await axios.post(`${API}/coberturas/ruta-puntos/guardar-permanente`, {
        fecha, chofer_id: f.chofer_id, zona: f.zona_dia,
      });
      const d = r.data || {};
      toast.success(`Ruta maestra actualizada: +${d.agregados || 0} agregados, −${d.removidos || 0} removidos`);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar permanente");
    } finally {
      setPermanenteGuardando((m) => ({ ...m, [idx]: false }));
    }
  };

  if (!filas) return <div className="text-slate-500 text-center py-8" data-testid="asig-cargando">Cargando…</div>;

  return (
    <div data-testid="tab-asignacion-dia" className="space-y-3">
      {/* Header */}
      <div className="card-base p-3" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>Fecha</label>
        <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="asig-fecha"
               style={{ padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 8 }}/>
        {!esHoy && (
          <span data-testid="asig-historial-badge"
                style={{ background: "#F1F5F9", color: "#475569", padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
            <i className="fa-solid fa-clock-rotate-left"/> Historial · solo lectura
          </span>
        )}
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#64748B", fontWeight: 700 }} data-testid="asig-contador">
          {filas.length} chofer{filas.length === 1 ? "" : "es"} · {cambiosDetectados.length} cambio{cambiosDetectados.length === 1 ? "" : "s"}
        </span>
      </div>

      {/* Tabla de filas */}
      <div className="card-base p-3" data-testid="asig-tabla">
        <div style={{ display: "grid", gridTemplateColumns: "minmax(180px,1fr) minmax(120px,1fr) minmax(120px,1fr) minmax(160px,1fr) 40px", gap: 10, padding: "6px 4px", fontSize: 11, fontWeight: 800, color: "#64748B", textTransform: "uppercase", borderBottom: "1px solid #E2E8F0" }}>
          <div>Chofer</div>
          <div>Pipa</div>
          <div>Zona base</div>
          <div>Parques del día</div>
          <div></div>
        </div>

        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {filas.map((f, idx) => {
            const tit = titulares[f.chofer_id] || {};
            const cambioPipa = f.pipa_id && f.pipa_id !== tit.pipa_titular;
            const cambioZona = f.zona_dia && f.zona_dia !== tit.zona_titular;
            const cambioParques = (f.removidos_set?.size || 0) + (f.agregados?.length || 0) > 0;
            const tieneCambio = cambioPipa || cambioZona || cambioParques;
            const pipaConflicto = f.pipa_id && (pipasAsignadas[f.pipa_id] || 0) > 1;
            const expandido = filaExpandida === idx;
            const nParquesEfectivos = (f.parques_base || []).filter((p) => !f.removidos_set.has(p.clave_ruta)).length + (f.agregados?.length || 0);

            return (
              <li key={f._k + ":" + idx} data-testid={`asig-fila-${idx}`}
                  style={{ background: tieneCambio ? "#FFF7ED" : "transparent", borderBottom: "1px solid #F1F5F9", padding: "10px 4px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(180px,1fr) minmax(120px,1fr) minmax(120px,1fr) minmax(160px,1fr) 40px", gap: 10, alignItems: "center" }}>
                  {/* Chofer */}
                  <div>
                    <select value={f.chofer_id}
                            onChange={(e) => { setFila(idx, { chofer_id: e.target.value, parques_loaded: false }); }}
                            disabled={!puedeEditar} data-testid={`asig-chofer-${idx}`}
                            style={{ width: "100%", border: "1px solid #CBD5E1", borderRadius: 8, padding: "8px 10px", fontSize: 13, background: "white" }}>
                      <option value="">— Selecciona —</option>
                      {choferesCatalogo.map((c) => (
                        <option key={c.id} value={c.id}>{c.nombre}</option>
                      ))}
                    </select>
                    {tit.pipa_titular && (
                      <div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>
                        Titular: #{tit.pipa_titular} · {tit.zona_titular || "—"}
                      </div>
                    )}
                  </div>

                  {/* Pipa */}
                  <div>
                    <select value={f.pipa_id}
                            onChange={(e) => setFila(idx, { pipa_id: e.target.value })}
                            disabled={!puedeEditar} data-testid={`asig-pipa-${idx}`}
                            style={{ width: "100%", border: `1px solid ${pipaConflicto ? "#DC2626" : "#CBD5E1"}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, background: "white", fontWeight: 700, color: cambioPipa ? "#9A3412" : "#0F172A" }}>
                      <option value="">— Sin pipa —</option>
                      {pipasCatalogo.map((p) => {
                        const enTaller = p.estado === "EN_TALLER" || p.estado === "TALLER" || p.en_taller === true;
                        return (
                          <option key={p.id_pipa} value={p.id_pipa} disabled={enTaller}
                                  style={enTaller ? { color: "#94A3B8" } : undefined}>
                            {enTaller ? "🔒 " : ""}#{p.id_pipa} {p.zona ? `· ${p.zona}` : ""}{enTaller ? " (en taller)" : ""}
                          </option>
                        );
                      })}
                    </select>
                    {pipaConflicto && <div style={{ fontSize: 10, color: "#DC2626", marginTop: 2, fontWeight: 700 }}>¡Duplicada!</div>}
                    {f.pipa_id && (pipasCatalogo.find(p => p.id_pipa === f.pipa_id)?.estado === "EN_TALLER") && (
                      <div style={{ fontSize: 10, color: "#92400E", marginTop: 2, fontWeight: 700 }}
                           data-testid={`asig-pipa-taller-warning-${idx}`}>
                        <i className="fa-solid fa-lock mr-1"/>Pipa en taller — selecciona otra
                      </div>
                    )}
                  </div>

                  {/* Zona base (dropdown) */}
                  <div>
                    <select value={f.zona_dia}
                            onChange={(e) => { setFila(idx, { zona_dia: e.target.value, parques_loaded: false }); }}
                            disabled={!puedeEditar} data-testid={`asig-zona-${idx}`}
                            style={{ width: "100%", border: "1px solid #CBD5E1", borderRadius: 8, padding: "8px 10px", fontSize: 13, background: "white", fontWeight: 700, color: cambioZona ? "#9A3412" : "#0F172A" }}>
                      <option value="">— Zona —</option>
                      {zonasCatalogo.map((z) => (
                        <option key={z} value={z}>{z}</option>
                      ))}
                    </select>
                  </div>

                  {/* Botón expandir parques */}
                  <div>
                    <button type="button" onClick={() => {
                                const next = expandido ? null : idx;
                                setFilaExpandida(next);
                                if (next !== null && !f.parques_loaded && f.chofer_id && f.zona_dia) {
                                  cargarParques(idx, f.chofer_id, f.zona_dia);
                                }
                              }}
                            disabled={!f.zona_dia || !f.chofer_id}
                            data-testid={`asig-parques-toggle-${idx}`}
                            style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${cambioParques ? "#EA580C" : "#CBD5E1"}`,
                                    background: cambioParques ? "#FFEDD5" : "white",
                                    color: cambioParques ? "#9A3412" : "#0F172A",
                                    fontWeight: 700, fontSize: 13,
                                    cursor: (!f.zona_dia || !f.chofer_id) ? "not-allowed" : "pointer" }}>
                      <i className={`fa-solid ${expandido ? "fa-chevron-up" : "fa-chevron-down"} mr-1`}/>
                      {f.parques_loaded ? (
                        <>{nParquesEfectivos} parque{nParquesEfectivos === 1 ? "" : "s"}</>
                      ) : (
                        <>Ver ruta</>
                      )}
                      {cambioParques && <span style={{ marginLeft: 6, fontSize: 10 }}>(+{f.agregados.length}/−{f.removidos_set.size})</span>}
                    </button>
                  </div>

                  {/* Eliminar */}
                  <button onClick={() => eliminarFila(idx)} disabled={!puedeEditar}
                          data-testid={`asig-eliminar-${idx}`} aria-label="Quitar fila"
                          style={{ background: "transparent", border: 0, color: "#94A3B8", cursor: puedeEditar ? "pointer" : "not-allowed", padding: 6, fontSize: 16 }}>
                    <i className="fa-solid fa-xmark"/>
                  </button>
                </div>

                {/* Panel expandido — Lista de parques + picker */}
                {expandido && (
                  <div data-testid={`asig-parques-panel-${idx}`}
                       style={{ marginTop: 10, padding: 12, background: "#F8FAFC", borderRadius: 8, border: "1px solid #E2E8F0" }}>
                    {!f.parques_loaded ? (
                      <div style={{ color: "#64748B", fontSize: 13 }}>Cargando parques…</div>
                    ) : (
                      <>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "#475569", marginBottom: 6 }}>
                          Ruta base · {f.zona_dia} · {(f.parques_base || []).length} parques
                        </div>
                        {(f.parques_base || []).length === 0 && (
                          <div style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic", marginBottom: 8 }}>
                            No hay parques base programados para este día.
                          </div>
                        )}
                        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 6 }}>
                          {(f.parques_base || []).map((p) => {
                            const removido = f.removidos_set.has(p.clave_ruta);
                            return (
                              <li key={p.clave_ruta}
                                  data-testid={`asig-parque-base-${idx}-${p.clave_ruta}`}
                                  style={{ display: "flex", alignItems: "center", gap: 8, background: removido ? "#FEE2E2" : "white", border: "1px solid #E2E8F0", padding: "6px 8px", borderRadius: 6, textDecoration: removido ? "line-through" : "none", opacity: removido ? 0.7 : 1 }}>
                                <input type="checkbox" checked={!removido}
                                       onChange={() => toggleRemovido(idx, p.clave_ruta)}
                                       disabled={!puedeEditar}/>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.nombre}</div>
                                  <div style={{ fontSize: 10, color: "#64748B" }}>{p.clave_ruta}{p.colonia ? ` · ${p.colonia}` : ""}</div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>

                        {/* Agregados (excepción) */}
                        {(f.agregados || []).length > 0 && (
                          <>
                            <div style={{ fontSize: 12, fontWeight: 800, color: "#047857", marginTop: 12, marginBottom: 6 }}>
                              + Agregados como excepción ({f.agregados.length})
                            </div>
                            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 6 }}>
                              {f.agregados.map((p) => (
                                <li key={p.clave_ruta} data-testid={`asig-parque-add-${idx}-${p.clave_ruta}`}
                                    style={{ display: "flex", alignItems: "center", gap: 8, background: "#D1FAE5", border: "1px solid #6EE7B7", padding: "6px 8px", borderRadius: 6 }}>
                                  <i className="fa-solid fa-plus" style={{ color: "#047857" }}/>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: "#065F46" }}>{p.nombre}</div>
                                    <div style={{ fontSize: 10, color: "#047857" }}>{p.clave_ruta}{p.colonia ? ` · ${p.colonia}` : ""}</div>
                                  </div>
                                  <button onClick={() => quitarAgregado(idx, p.clave_ruta)} disabled={!puedeEditar}
                                          style={{ background: "transparent", border: 0, color: "#047857", cursor: "pointer" }}>
                                    <i className="fa-solid fa-xmark"/>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}

                        {/* Picker para agregar */}
                        {puedeEditar && (
                          <div style={{ marginTop: 12, padding: 10, background: "white", borderRadius: 6, border: "1px dashed #94A3B8" }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: "#475569", marginBottom: 6 }}>
                              Agregar parque excepcional del día
                            </div>
                            <input type="text" value={f.picker_query || ""}
                                   onChange={(e) => buscarParques(idx, e.target.value)}
                                   placeholder="Buscar por nombre, ID o colonia (mín 2 letras)…"
                                   data-testid={`asig-picker-input-${idx}`}
                                   style={{ width: "100%", padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }}/>
                            {(f.picker_results || []).length > 0 && (
                              <ul style={{ listStyle: "none", margin: "8px 0 0", padding: 0, maxHeight: 220, overflow: "auto", border: "1px solid #E2E8F0", borderRadius: 6 }}>
                                {f.picker_results.map((esp) => (
                                  <li key={esp.id_oficial}
                                      onClick={() => agregarParque(idx, esp)}
                                      data-testid={`asig-picker-item-${idx}-${esp.id_oficial}`}
                                      style={{ padding: "6px 10px", cursor: "pointer", borderBottom: "1px solid #F1F5F9", fontSize: 12 }}
                                      onMouseEnter={(e) => e.currentTarget.style.background = "#F8FAFC"}
                                      onMouseLeave={(e) => e.currentTarget.style.background = "white"}>
                                    <span style={{ fontWeight: 700 }}>{esp.nombre}</span> <span style={{ color: "#64748B" }}>· {esp.id_oficial} · {esp.zona_id}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}

                        {/* Botón guardar permanente */}
                        {puedeEditar && (f.removidos_set?.size > 0 || f.agregados?.length > 0) && (
                          <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
                            <button onClick={() => guardarPermanente(idx)}
                                    disabled={permanenteGuardando[idx]}
                                    data-testid={`asig-guardar-permanente-${idx}`}
                                    style={{ background: permanenteGuardando[idx] ? "#94A3B8" : "#7C3AED", color: "white", border: 0, padding: "8px 14px", borderRadius: 8, fontWeight: 800, fontSize: 12, cursor: permanenteGuardando[idx] ? "wait" : "pointer" }}>
                              <i className="fa-solid fa-anchor mr-2"/>
                              {permanenteGuardando[idx] ? "Guardando…" : "Guardar permanente en ruta maestra"}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {puedeEditar && (
          <div style={{ paddingTop: 12 }}>
            <button onClick={agregarFila} data-testid="asig-agregar"
                    style={{ background: "white", border: "1px dashed #94A3B8", color: "#475569", padding: "8px 14px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
              <i className="fa-solid fa-plus mr-1"/> Agregar chofer
            </button>
          </div>
        )}
      </div>

      {/* Resumen + acciones */}
      <div className="card-base p-3" data-testid="asig-resumen"
           style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ fontSize: 13, color: "#0F172A" }}>
            <strong>{filas.length}</strong> filas
          </div>
          <div style={{ fontSize: 13, color: cambiosDetectados.length ? "#9A3412" : "#15803D", fontWeight: 800 }} data-testid="asig-cambios-num">
            <i className={`fa-solid ${cambiosDetectados.length ? "fa-rotate" : "fa-check"} mr-1`}/>
            {cambiosDetectados.length} cambio{cambiosDetectados.length === 1 ? "" : "s"} detectado{cambiosDetectados.length === 1 ? "" : "s"}
          </div>
        </div>
        <button onClick={guardar} disabled={!puedeEditar || guardando}
                data-testid="asig-guardar-notificar"
                style={{ background: puedeEditar && !guardando ? "#1D4ED8" : "#94A3B8", color: "white", border: 0, padding: "12px 18px", borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: puedeEditar && !guardando ? "pointer" : "not-allowed" }}>
          <i className="fa-solid fa-paper-plane mr-2"/>
          {guardando ? "Guardando…" : "Guardar y notificar"}
        </button>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   TAB · Mapa de Zonas
   Stats + Google My Maps embed + lista Z1..Z20 con bottom-sheet
   de asignación rápida.
   ═══════════════════════════════════════════════════════════ */
// Mapa Leaflet — nube de puntos con popup al tocar
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

function MapaZonasLeaflet({ puntos }) {
  return (
    <MapContainer
      center={[28.6353, -106.0889]}
      zoom={11}
      style={{ height: 340, width: "100%", borderRadius: 8 }}
      scrollWheelZoom={false}
    >
      <TileLayer
        attribution='&copy; OpenStreetMap'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {puntos.map((p, i) => (
        <CircleMarker
          key={i}
          center={[p.lat, p.lng]}
          radius={5}
          pathOptions={{ color: "#fff", weight: 1, fillColor: p.color, fillOpacity: 0.85 }}
        >
          <Popup>
            <div style={{ minWidth: 160 }}>
              <span style={{
                display: "inline-block", fontSize: 11, fontWeight: 500,
                padding: "2px 8px", borderRadius: 20, marginBottom: 6,
                background: p.color + "22", color: p.color, border: `1px solid ${p.color}55`
              }}>{p.zona_label || p.zona}</span>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, lineHeight: 1.3 }}>{p.nombre}</div>
              {p.colonia ? <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                <span style={{ color: "#999" }}>Colonia</span> {p.colonia}
              </div> : null}
              <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
                <span style={{ color: "#999" }}>Pipa</span> #{p.pipa_id}
              </div>
              <a
                href={`https://www.google.com/maps?q=${p.lat},${p.lng}`}
                target="_blank" rel="noreferrer"
                style={{ fontSize: 12, color: "#1a73e8", display: "block", textAlign: "center",
                  borderTop: "1px solid #eee", paddingTop: 6, marginTop: 4 }}
              >Ver en Google Maps</a>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </MapContainer>
  );
}

const ZONA_ESTADO = {
  OK:          { label: "OK",          bg: "#DCFCE7", fg: "#166534", chip: "#1D9E75" },
  SIN_CHOFER:  { label: "Sin chofer",  bg: "#FED7AA", fg: "#9A3412", chip: "#D97706" },
  SIN_PIPA:    { label: "Sin pipa",    bg: "#FFEDD5", fg: "#9A3412", chip: "#EA580C" },
  EN_TALLER:   { label: "En taller",   bg: "#FECACA", fg: "#991B1B", chip: "#DC2626" },
};

function TabMapaZonas({ user }) {
  const [data, setData] = useState(null);
  const [puntos, setPuntos] = useState([]);
  const [seleccion, setSeleccion] = useState(null);
  const puedeEditar = ["admin", "supervisor_general_pipas", "supervisor_pipas"].includes(user?.rol);

  const cargar = useCallback(async () => {
    try {
      const [rZonas, rPuntos] = await Promise.all([
        axios.get(`${API}/coberturas/zonas-resumen`),
        axios.get(`${API}/coberturas/zonas-puntos`),
      ]);
      setData(rZonas.data);
      setPuntos(rPuntos.data.puntos || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando zonas");
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  if (!data) return <div className="text-slate-500 text-center py-8">Cargando…</div>;
  const s = data.stats;

  return (
    <div data-testid="tab-mapa-zonas">
      {/* SECCIÓN 1 — Stats */}
      <div className="mz-stats">
        <div className="mz-stat" data-testid="mz-stat-asignadas" style={{ borderLeftColor: "#1D9E75" }}>
          <div className="mz-stat-n" style={{ color: "#1D9E75" }}>{s.zonas_asignadas}</div>
          <div className="mz-stat-l">Zonas asignadas hoy</div>
        </div>
        <div className="mz-stat" data-testid="mz-stat-sin-chofer" style={{ borderLeftColor: "#D97706" }}>
          <div className="mz-stat-n" style={{ color: "#D97706" }}>{s.zonas_sin_chofer}</div>
          <div className="mz-stat-l">Zonas sin chofer</div>
        </div>
        <div className="mz-stat" data-testid="mz-stat-taller" style={{ borderLeftColor: "#DC2626" }}>
          <div className="mz-stat-n" style={{ color: "#DC2626" }}>{s.pipas_en_taller}</div>
          <div className="mz-stat-l">Pipas en taller</div>
        </div>
      </div>

      {/* SECCIÓN 2 — Mapa */}
      <div className="mz-mapa-wrap card-base" data-testid="mz-mapa">
        {puntos.length > 0
          ? <MapaZonasLeaflet puntos={puntos} />
          : <div style={{ height: 340, display: "flex", alignItems: "center", justifyContent: "center", color: "#888" }}>Cargando mapa…</div>
        }
        <div className="mz-mapa-tip">
          <i className="fa-solid fa-hand-pointer"/> Pellizca para zoom · Toca un punto para ver detalle
        </div>
      </div>

      {/* SECCIÓN 3 — Lista Z1..Z14 */}
      <h3 className="mz-h">Zonas Z1 → Z14</h3>
      <ul className="mz-lista" data-testid="mz-lista">
        {data.items.filter(z => !z.tipo).map((z) => {
          const est = ZONA_ESTADO[z.estado] || ZONA_ESTADO.OK;
          return (
            <li key={z.zona}>
              <button
                className="mz-fila"
                onClick={() => puedeEditar && setSeleccion(z)}
                disabled={!puedeEditar}
                data-testid={`mz-fila-${z.zona}`}
                style={{ cursor: puedeEditar ? "pointer" : "default" }}
              >
                <span className="mz-chip" style={{ background: est.chip }}>{z.zona}</span>
                <div className="mz-fila-body">
                  <div className="mz-fila-pipa">
                    <i className="fa-solid fa-truck-droplet"/> #{z.pipa_hoy || "—"}
                    {z.es_cambio ? (
                      <span data-testid={`mz-badge-hoy-${z.zona}`} style={{
                        marginLeft: 6, padding: "2px 8px", borderRadius: 999,
                        background: "#FED7AA", color: "#9A3412",
                        fontSize: 10, fontWeight: 800, letterSpacing: 0.3,
                      }}>HOY</span>
                    ) : (z.pipa_titular && z.chofer_titular_id) ? (
                      <span data-testid={`mz-badge-titular-${z.zona}`} style={{
                        marginLeft: 6, padding: "2px 8px", borderRadius: 999,
                        background: "#DCFCE7", color: "#166534",
                        fontSize: 10, fontWeight: 800, letterSpacing: 0.3,
                      }}>TITULAR</span>
                    ) : null}
                  </div>
                  <div className="mz-fila-chofer">
                    {z.chofer_hoy_nombre || <em style={{ color: "#94A3B8" }}>Sin chofer</em>}
                  </div>
                </div>
                <div className="mz-fila-meta">
                  <div className="mz-parques"><strong>{z.parques_count}</strong> parques</div>
                  <span className="mz-badge" style={{ background: est.bg, color: est.fg }}>
                    {est.label}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {/* SECCIÓN 4 — Zonas Especiales */}
      {data.items.some(z => z.tipo) && (
        <>
          <h3 className="mz-h" style={{ marginTop: 16 }}>Zonas Especiales</h3>
          <ul className="mz-lista" data-testid="mz-lista-especiales">
            {data.items.filter(z => z.tipo).map((z) => {
              const est = ZONA_ESTADO[z.estado] || ZONA_ESTADO.OK;
              const chipColor = z.tipo === "TINACO" ? "#0EA5E9" : "#059669";
              return (
                <li key={z.zona}>
                  <button
                    className="mz-fila"
                    onClick={() => puedeEditar && setSeleccion(z)}
                    disabled={!puedeEditar}
                    data-testid={`mz-fila-${z.zona}`}
                    style={{ cursor: puedeEditar ? "pointer" : "default" }}
                  >
                    <span className="mz-chip" style={{ background: chipColor, fontSize: 10 }}>
                      {z.tipo === "TINACO" ? "💧" : "🌿"} {z.label || z.zona}
                    </span>
                    <div className="mz-fila-body">
                      <div className="mz-fila-pipa">
                        <i className="fa-solid fa-truck-droplet"/> #{z.pipa_hoy || "—"}
                      </div>
                      <div className="mz-fila-chofer">
                        {z.chofer_hoy_nombre || <em style={{ color: "#94A3B8" }}>Sin chofer</em>}
                      </div>
                    </div>
                    <div className="mz-fila-meta">
                      <div className="mz-parques"><strong>{z.parques_count}</strong> puntos</div>
                      <span className="mz-badge" style={{ background: est.bg, color: est.fg }}>
                        {est.label}
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {seleccion && (
        <BottomSheetZona
          fecha={data.fecha}
          zona={seleccion}
          user={user}
          onClose={() => setSeleccion(null)}
          onGuardado={() => { setSeleccion(null); cargar(); }}
        />
      )}
    </div>
  );
}

function BottomSheetZona({ fecha, zona, user, onClose, onGuardado }) {
  const [choferes, setChoferes] = useState(null);
  const [pipas, setPipas] = useState(null);
  // Mapa pipa_id → zona_titular (para mostrar "titular Z14" en choferes)
  const [pipaZonaMap, setPipaZonaMap] = useState({});
  // null = "Sin cobertura hoy"
  const [choferId, setChoferId] = useState(zona.chofer_hoy_id || zona.chofer_titular_id || "");
  const [pipaId, setPipaId] = useState(zona.pipa_hoy || zona.pipa_titular || "");
  const [guardando, setGuardando] = useState(false);
  // Modo: "hoy" (cobertura temporal) | "titular" (cambio permanente)
  // Solo admin y supervisor_general_pipas pueden cambiar titular permanente.
  const puedeCambiarTitular = ["admin", "supervisor_general_pipas"].includes(user?.rol);
  const [modo, setModo] = useState("hoy");

  useEffect(() => {
    (async () => {
      try {
        const [ch, pi] = await Promise.all([
          axios.get(`${API}/coberturas/disponibles`, { params: { fecha, tipo: "chofer" } }),
          axios.get(`${API}/coberturas/disponibles`, { params: { fecha, tipo: "pipa", incluir_taller: true } }),
        ]);

        // ── Pipas: solo TRATADA con zona Z1..Z14 ────────────────
        const Z_VALIDA = /^Z(?:[1-9]|1[0-3]|14)$/;
        const pipasFiltradas = (pi.data.items || []).filter(p =>
          (p.tipo_agua || "").toUpperCase() === "TRATADA" &&
          Z_VALIDA.test(p.zona_titular || "")
        );
        // Asegurar pipa titular de esta zona
        const pipIds = new Set(pipasFiltradas.map(p => p.id_pipa));
        if (zona.pipa_titular && !pipIds.has(zona.pipa_titular)) {
          pipasFiltradas.unshift({
            id_pipa: zona.pipa_titular,
            zona_titular: zona.zona,
            tipo_agua: "TRATADA",
            estado: "OPERATIVA",
          });
        }
        // Orden: la titular de esta zona PRIMERO, luego por número de zona
        pipasFiltradas.sort((a, b) => {
          if (a.id_pipa === zona.pipa_titular) return -1;
          if (b.id_pipa === zona.pipa_titular) return 1;
          const na = parseInt((a.zona_titular || "").replace("Z", ""), 10) || 999;
          const nb = parseInt((b.zona_titular || "").replace("Z", ""), 10) || 999;
          if (na !== nb) return na - nb;
          return String(a.id_pipa).localeCompare(String(b.id_pipa));
        });
        setPipas(pipasFiltradas);

        // Mapa pipa → zona (para mostrar zona titular del chofer)
        const map = {};
        for (const p of (pi.data.items || [])) {
          if (p.id_pipa && p.zona_titular) map[p.id_pipa] = p.zona_titular;
        }
        setPipaZonaMap(map);

        // ── Choferes: disponibles + asegurar titular de esta zona ─
        const chofIds = new Set((ch.data.items || []).map(c => c.id));
        const lista = [...(ch.data.items || [])];
        if (zona.chofer_titular_id && !chofIds.has(zona.chofer_titular_id)) {
          lista.unshift({
            id: zona.chofer_titular_id,
            nombre: zona.chofer_titular_nombre,
            n_empleado: "",
            pipa_titular: zona.pipa_titular,
          });
        }
        // Orden: titular de esta zona PRIMERO, luego alfabético
        lista.sort((a, b) => {
          if (a.id === zona.chofer_titular_id) return -1;
          if (b.id === zona.chofer_titular_id) return 1;
          return (a.nombre || "").localeCompare(b.nombre || "");
        });
        setChoferes(lista);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error cargando opciones");
      }
    })();
  }, [fecha, zona]);

  // Helper: dado un pipa_titular del chofer, devolver "Z14" o ""
  const zonaDeChofer = (c) => {
    if (!c?.pipa_titular) return "";
    return pipaZonaMap[c.pipa_titular] || "";
  };

  const choferSelObj = (choferes || []).find(c => c.id === choferId);
  const pipaSelObj = (pipas || []).find(p => p.id_pipa === pipaId);
  const esChoferDeOtraZona = choferSelObj && choferSelObj.id !== zona.chofer_titular_id && zona.chofer_titular_id;
  const esPipaDeOtraZona = pipaSelObj && pipaSelObj.id_pipa !== zona.pipa_titular && zona.pipa_titular;

  const guardar = async () => {
    if (!pipaId) return toast.error("Selecciona una pipa");
    setGuardando(true);
    try {
      await axios.post(`${API}/coberturas/asignar-zona`, {
        fecha, zona: zona.zona, pipa_id: pipaId,
        chofer_id: choferId || null,
      });
      toast.success(`Zona ${zona.zona} asignada`);
      onGuardado();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setGuardando(false); }
  };

  const cambiarTitularPermanente = async () => {
    if (!pipaId) return toast.error("Selecciona una pipa");
    if (!choferId) return toast.error("Selecciona un chofer titular");
    const chof = choferSelObj?.nombre || "el chofer seleccionado";
    if (!window.confirm(
      `⚠️ CAMBIO PERMANENTE\n\n` +
      `${zona.zona} pasará a ser titular de ${chof} con pipa #${pipaId}.\n\n` +
      `El chofer anterior (${zona.chofer_titular_nombre || "sin titular previo"}) quedará SIN PIPA.\n\n` +
      `¿Confirmar?`
    )) return;
    setGuardando(true);
    try {
      const r = await axios.post(`${API}/coberturas/cambiar-titular-permanente`, {
        zona: zona.zona,
        pipa_id: pipaId,
        chofer_id: choferId,
      });
      toast.success(
        `Titular de ${zona.zona} actualizado` +
        (r.data?.chofer_liberado ? ` · ${r.data.chofer_liberado} quedó liberado` : "")
      );
      onGuardado();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cambiar titular");
    } finally { setGuardando(false); }
  };

  const volverTitular = async () => {
    if (!window.confirm(
      `¿Volver al titular de ${zona.zona}?\n\n${zona.chofer_titular_nombre || "Chofer titular"} con pipa #${zona.pipa_titular || "—"}.`
    )) return;
    setGuardando(true);
    try {
      await axios.post(`${API}/coberturas/restaurar-zona`, {
        fecha, zona: zona.zona,
      });
      toast.success(`Zona ${zona.zona} restaurada al titular`);
      onGuardado();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al restaurar");
    } finally { setGuardando(false); }
  };

  // ¿Hay cambio activo en la zona? (chofer o pipa diferente al titular)
  const hayCambioActivo = !!zona.es_cambio;

  return (
    <div className="sup-modal-bg" onClick={onClose} data-testid="bs-zona-bg">
      <div onClick={(e) => e.stopPropagation()} data-testid="bs-zona"
        style={{
          background: "white", borderRadius: "16px 16px 0 0", padding: 16,
          position: "fixed", left: 0, right: 0, bottom: 0, maxHeight: "85vh",
          display: "flex", flexDirection: "column", boxShadow: "0 -8px 20px rgba(0,0,0,0.15)",
          overflowY: "auto",
        }}
      >
        <div style={{ width: 40, height: 4, background: "#CBD5E1", borderRadius: 99, margin: "0 auto 12px" }}/>
        <header style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0F172A" }} data-testid="bs-zona-titulo">
            {zona.zona} · {zona.parques_count} parques — ¿Quién va hoy?
          </div>
          {zona.chofer_titular_nombre && (
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
              Titular: <strong>{zona.chofer_titular_nombre}</strong>
              {zona.pipa_titular ? <> · Pipa <strong>#{zona.pipa_titular}</strong></> : null}
            </div>
          )}
          {hayCambioActivo && (
            <div style={{
              marginTop: 10, padding: "8px 12px", borderRadius: 10,
              background: "#FEF3C7", border: "1px solid #FDE68A",
              display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
            }} data-testid="bs-zona-cambio-activo">
              <i className="fa-solid fa-shuffle" style={{ color: "#B45309" }}/>
              <span style={{ fontSize: 12, color: "#92400E", fontWeight: 700, flex: 1, minWidth: 0 }}>
                Hay un cambio activo en {zona.zona} hoy
              </span>
              <button
                onClick={volverTitular}
                disabled={guardando}
                data-testid="bs-zona-volver-titular"
                style={{
                  background: "#B45309", color: "white", border: 0,
                  padding: "6px 12px", borderRadius: 8, fontWeight: 800,
                  fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
                }}
              >
                <i className="fa-solid fa-rotate-left" style={{ marginRight: 4 }}/>
                Volver al titular
              </button>
            </div>
          )}
        </header>

        {/* ── Tabs de modo (solo admin/sup. general) ────────────── */}
        {puedeCambiarTitular && (
          <div data-testid="bs-zona-modo-tabs" style={{
            display: "flex", background: "#F1F5F9", borderRadius: 10,
            padding: 4, marginBottom: 14, gap: 4,
          }}>
            <button
              type="button"
              onClick={() => setModo("hoy")}
              data-testid="bs-zona-modo-hoy"
              style={{
                flex: 1, padding: "10px 8px", borderRadius: 8,
                border: 0, fontWeight: 700, fontSize: 13,
                background: modo === "hoy" ? "white" : "transparent",
                color: modo === "hoy" ? "#0F172A" : "#64748B",
                boxShadow: modo === "hoy" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                cursor: "pointer",
              }}
            >
              <i className="fa-solid fa-calendar-day mr-1"/> Cubrir hoy
            </button>
            <button
              type="button"
              onClick={() => setModo("titular")}
              data-testid="bs-zona-modo-titular"
              style={{
                flex: 1, padding: "10px 8px", borderRadius: 8,
                border: 0, fontWeight: 700, fontSize: 13,
                background: modo === "titular" ? "white" : "transparent",
                color: modo === "titular" ? "#0F172A" : "#64748B",
                boxShadow: modo === "titular" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                cursor: "pointer",
              }}
            >
              <i className="fa-solid fa-shield-halved mr-1"/> Cambiar titular
            </button>
          </div>
        )}

        {modo === "titular" && (
          <div data-testid="bs-zona-aviso-titular" style={{
            background: "#FEE2E2", border: "1px solid #FCA5A5",
            color: "#991B1B", padding: "10px 12px", borderRadius: 10,
            fontSize: 12, fontWeight: 600, lineHeight: 1.4, marginBottom: 14,
          }}>
            <i className="fa-solid fa-triangle-exclamation mr-1"/>
            <strong>Esto cambiará el titular permanente de {zona.zona}.</strong> El chofer
            seleccionado verá esta zona como su ruta fija desde HOY. El chofer anterior
            quedará SIN PIPA hasta que un admin le asigne otra.
          </div>
        )}

        <div style={{ display: "grid", gap: 14 }}>
          {/* ── Chofer ─────────────────────────────────────────── */}
          <label style={{ display: "block" }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4 }}>
              ¿Quién va a esta zona hoy?
            </span>
            <select value={choferId} onChange={(e) => setChoferId(e.target.value)} data-testid="bs-zona-chofer"
                    style={{ width: "100%", padding: 12, border: "1px solid #CBD5E1", borderRadius: 8, marginTop: 6, fontSize: 15, background: "white" }}>
              <option value="">— Sin cobertura hoy —</option>
              {(choferes || []).map((c) => {
                const esTitular = c.id === zona.chofer_titular_id;
                const zChofer = zonaDeChofer(c);
                return (
                  <option key={c.id} value={c.id} data-testid={`bs-zona-chofer-opt-${c.id}`}>
                    {c.nombre || c.n_empleado}
                  </option>
                );
              })}
            </select>
            {choferSelObj && choferId === zona.chofer_titular_id && (
              <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 800,
                            background: "#DCFCE7", color: "#166534", padding: "3px 10px", borderRadius: 999 }} data-testid="bs-zona-badge-chofer-titular">
                <i className="fa-solid fa-shield-halved"/> TITULAR de {zona.zona}
              </div>
            )}
            {esChoferDeOtraZona && (
              <div style={{ marginTop: 6, padding: "6px 10px", borderRadius: 8, background: "#EFF6FF",
                            color: "#1E40AF", fontSize: 12, fontWeight: 600, border: "1px solid #BFDBFE" }} data-testid="bs-zona-info-cobertura">
                <i className="fa-solid fa-circle-info mr-1"/>
                {choferSelObj.nombre || "Chofer"} {zonaDeChofer(choferSelObj) ? <>es titular de <strong>{zonaDeChofer(choferSelObj)}</strong>. </> : null}
                Hoy cubrirá <strong>{zona.zona}</strong> y verá ese banner en su app.
              </div>
            )}
          </label>

          {/* ── Pipa ───────────────────────────────────────────── */}
          <label style={{ display: "block" }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4 }}>
              ¿Con qué pipa sale?
            </span>
            <select value={pipaId} onChange={(e) => setPipaId(e.target.value)} data-testid="bs-zona-pipa"
                    style={{ width: "100%", padding: 12, border: "1px solid #CBD5E1", borderRadius: 8, marginTop: 6, fontSize: 15, background: "white" }}>
              <option value="">— elige pipa —</option>
              {(pipas || []).map((p) => {
                const esTitularDeEstaZona = p.id_pipa === zona.pipa_titular;
                const enTaller = p.estado === "EN_TALLER";
                const z = p.zona_titular || "—";
                let extra;
                if (enTaller) extra = " (en taller)";
                else if (esTitularDeEstaZona) extra = " · TITULAR";
                else extra = ` · titular ${z}`;
                return (
                  <option
                    key={p.id_pipa}
                    value={p.id_pipa}
                    disabled={enTaller}
                    data-testid={`bs-zona-opt-${p.id_pipa}`}
                    style={enTaller ? { color: "#94A3B8", fontStyle: "italic" } : undefined}
                  >
                    #{p.id_pipa}{extra}
                  </option>
                );
              })}
            </select>
            {pipaSelObj && pipaId === zona.pipa_titular && (
              <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 800,
                            background: "#DCFCE7", color: "#166534", padding: "3px 10px", borderRadius: 999 }} data-testid="bs-zona-badge-pipa-titular">
                <i className="fa-solid fa-truck-droplet"/> Pipa TITULAR de {zona.zona}
              </div>
            )}
            {esPipaDeOtraZona && (
              <div style={{ marginTop: 6, padding: "6px 10px", borderRadius: 8, background: "#FEF3C7",
                            color: "#92400E", fontSize: 12, fontWeight: 600, border: "1px solid #FDE68A" }}>
                <i className="fa-solid fa-shuffle mr-1"/>
                Pipa <strong>#{pipaSelObj.id_pipa}</strong> es titular de <strong>{pipaSelObj.zona_titular}</strong>. Hoy se usa en {zona.zona}.
              </div>
            )}
          </label>
        </div>

        <div style={{ marginTop: 18, display: "flex", gap: 8 }}>
          <button onClick={onClose} data-testid="bs-zona-cancelar"
                  style={{ flex: 1, background: "white", border: "1px solid #CBD5E1", color: "#334155", padding: 14, borderRadius: 10, fontWeight: 700, cursor: "pointer" }}>
            Cancelar
          </button>
          {modo === "titular" ? (
            <button
              onClick={cambiarTitularPermanente}
              disabled={guardando}
              data-testid="bs-zona-confirmar-titular"
              style={{ flex: 2, background: "#DC2626", color: "white", border: 0, padding: 14, borderRadius: 10, fontWeight: 800, fontSize: 15, cursor: "pointer" }}
            >
              <i className="fa-solid fa-shield-halved mr-1"/>
              {guardando ? "Aplicando…" : "Confirmar cambio permanente"}
            </button>
          ) : (
            <button onClick={guardar} disabled={guardando} data-testid="bs-zona-confirmar"
                    style={{ flex: 2, background: "#1D4ED8", color: "white", border: 0, padding: 14, borderRadius: 10, fontWeight: 800, fontSize: 15, cursor: "pointer" }}>
              {guardando ? "Guardando…" : "Confirmar"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function fmtChih(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      timeZone: "America/Chihuahua",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    });
  } catch { return iso.slice(0, 19).replace("T", " "); }
}

/* ─────────────────── TabActividadChoferes ───────────────────
   Solicitado 12-jun-2026 por Octavio. Solo lectura.
   - Visible para admin + supervisor_general_pipas (saul, rogelio, LE, edgari).
   - Filtros: nombre chofer (parcial), pipa (parcial/exacto), fecha desde/hasta.
   - Botón Descargar Excel con los resultados filtrados. */
export function TabActividadChoferes({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  // Pre-fill "desde" con hace 30 días para que la carga inicial no sea sobre toda la colección
  const _hace30 = (() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0,10); })();
  const [filtros, setFiltros] = useState({
    chofer: "", pipa: "", desde: _hace30, hasta: "",
  });
  // Modal para ver fotos en grande
  const [fotoModal, setFotoModal] = useState(null);

  const TIPO_ATENCION_BADGE = {
    ordinaria: { bg: "#E0E7FF", fg: "#3730A3", label: "Ordinaria" },
    crc:       { bg: "#FEF3C7", fg: "#92400E", label: "CRC" },
    oficio:    { bg: "#DCFCE7", fg: "#15803D", label: "Oficio" },
    emergencia:{ bg: "#FEE2E2", fg: "#B91C1C", label: "Emergencia" },
  };

  const esAdmin = user?.rol === "admin";
  const puedeGestionar = ["admin", "supervisor_general_pipas", "supervisor_pipas", "capturista"].includes(user?.rol);

  // Mapeo legible para la columna "Tipo evento"
  const TIPO_EVENTO_LABEL = {
    // mayúsculas (nuevos registros)
    "RIEGO":                      "Riego",
    "TALLER":                     "Taller",
    "TINACO DE AGUA POTABLE":     "Tinaco agua potable (llenado)",
    "TINACO DE AGUA TRATADA":     "Tinaco agua tratada (llenado)",
    "LLENADO EN LA GARZA":        "Llenado en la garza",
    "LLENADO DE PILETA":          "Llenado de pileta (panteones)",
    "APOYO DE BOMBEROS":          "Apoyo de bomberos",
    // minúsculas (registros históricos — se mantienen legibles)
    riego:                        "Riego",
    llenado_tinaco:               "Llenado de tinaco",
    llenado_garza:                "Llenado garza",
    llenado_pileta:               "Llenado pileta",
    apoyo_bomberos:               "Ayuda bomberos",
    taller:                       "Taller",
    registrar_punto:              "Registrar punto",
    ruta_validacion:     "Ruta validada",
    atencion_prioridad:  "Atención prioridad",
    prioridad_no_pudo:   "Prioridad no atendida",
    intervencion_punto:  "Estoy en un punto",
  };

  const ESTADO_BADGE = {
    PENDIENTE:  { bg: "#FEF3C7", fg: "#92400E", label: "Pendiente" },
    VALIDADO:   { bg: "#DCFCE7", fg: "#15803D", label: "Validado"  },
    RECHAZADO:  { bg: "#FEE2E2", fg: "#B91C1C", label: "Rechazado" },
  };

  // Edición inline de "Lugar atendido"
  const [editandoLugar, setEditandoLugar] = useState(null); // {id, valor}
  const [guardandoLugar, setGuardandoLugar] = useState(false);

  // Clasificación inline de tipo_espacio en catalogo_espacios
  const TIPOS_ESPACIO_OPCIONES = [
    "PARQUE","JARDÍN","ÁREA BARRIO","CAMELLÓN","GLORIETA","ISLETA",
    "INSTITUCIONAL","PLAZA","ESPACIO MASIVO","DEPORTIVO/CANCHA","TRIÁNGULO",
    "RELIGIOSO","EDUCATIVO","PANTEÓN","POZO/TANQUE","OFICIAL","SALUD",
    "ALJIBE","TINACO","PILETA","FUENTE","ARROYO","BULEVARD",
    "CAMINO","CENTRO COMUNITARIO","DEPENDENCIA DE GOBIERNO",
  ];
  const [clasificandoEspacio, setClasificandoEspacio] = useState(null); // evento.id
  const [guardandoTipo, setGuardandoTipo] = useState(false);
  const guardarTipoEspacio = async (evento, tipo) => {
    setGuardandoTipo(true);
    try {
      await axios.patch(
        `${API}/admin/catalogo-espacios-oid/${evento.espacio_id}/tipo-espacio`,
        { tipo_espacio: tipo },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
      );
      toast.success(`Tipo "${tipo}" guardado`);
      setItems(prev => prev.map(it =>
        it.id === evento.id
          ? { ...it, tipo_espacio_lugar: tipo, necesita_clasificacion: false }
          : it
      ));
      setClasificandoEspacio(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setGuardandoTipo(false);
    }
  };


  // Corrección de resultado (atendido/no atendido) — solo crc_oficio
  const [editandoResultado, setEditandoResultado] = useState(null); // {id, resultado, motivo}
  const [guardandoResultado, setGuardandoResultado] = useState(false);

  const guardarResultado = async () => {
    if (!editandoResultado) return;
    if (editandoResultado.resultado === "NO_ATENDIDO" && !editandoResultado.motivo.trim()) {
      toast.error("Escribe el motivo antes de guardar");
      return;
    }
    setGuardandoResultado(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/eventos-chofer/${editandoResultado.id}/resultado`,
        { resultado: editandoResultado.resultado, motivo: editandoResultado.motivo },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
      );
      toast.success("Resultado corregido");
      setItems(prev => prev.map(it =>
        it.id === editandoResultado.id
          ? {
              ...it,
              // CRC: actualiza estado; ordinario: actualiza atendido bool
              ...(data.estado_nuevo !== undefined ? { estado: data.estado_nuevo } : {}),
              ...(data.atendido_nuevo !== undefined ? { atendido: data.atendido_nuevo } : {}),
              motivo_no_atendido: data.motivo || null,
            }
          : it
      ));
      setEditandoResultado(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setGuardandoResultado(false);
    }
  };

  const puedeCorregirResultado = ["admin", "supervisor_general_pipas", "supervisor_pipas", "capturista"].includes(user?.rol);

  const guardarLugar = async () => {
    if (!editandoLugar) return;
    setGuardandoLugar(true);
    try {
      await axios.patch(
        `${API}/admin/eventos-chofer/${editandoLugar.id}/lugar`,
        { nombre_lugar: editandoLugar.valor },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
      );
      toast.success("Lugar corregido");
      setItems(prev => prev.map(it =>
        it.id === editandoLugar.id
          ? { ...it, nombre_lugar: editandoLugar.valor, lugar_atendido: editandoLugar.valor }
          : it
      ));
      setEditandoLugar(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setGuardandoLugar(false);
    }
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.chofer) params.append("chofer", filtros.chofer);
      if (filtros.pipa) params.append("pipa", filtros.pipa);
      if (filtros.desde) params.append("fecha_inicio", filtros.desde);
      if (filtros.hasta) params.append("fecha_fin", filtros.hasta);
      params.append("limit", "1000");
      const r = await axios.get(`${API}/admin/eventos-chofer?${params.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la actividad");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { let a = true; (async () => { await Promise.resolve(); if (a) await cargar(); })(); return () => { a = false; }; }, []);

  const aplicarFiltros = (e) => { e.preventDefault(); cargar(); };

  const descargarExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.chofer) params.append("chofer", filtros.chofer);
      if (filtros.pipa) params.append("pipa", filtros.pipa);
      if (filtros.desde) params.append("fecha_inicio", filtros.desde);
      if (filtros.hasta) params.append("fecha_fin", filtros.hasta);
      const r = await axios.get(`${API}/admin/eventos-chofer/excel?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `actividad_choferes_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch {
      toast.error("Error al descargar Excel");
    }
  };

  const setF = (k) => (e) => setFiltros(p => ({ ...p, [k]: e.target.value }));

  return (
    <div className="card-base p-4 bg-white" data-testid="tab-actividad-content">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-base font-bold text-slate-800">
          <i className="fa-solid fa-user-clock mr-2 text-indigo-600"/>
          Actividad de Choferes
          <span className="text-xs font-normal text-slate-500 ml-2">
            (lectura · {total} eventos)
          </span>
        </h3>
        <button onClick={descargarExcel} data-testid="btn-descargar-excel"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded px-3 py-2 text-sm">
          <i className="fa-solid fa-file-excel mr-1"/> Descargar Excel
        </button>
      </div>

      <form onSubmit={aplicarFiltros} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3" data-testid="actividad-filtros">
        <input placeholder="Nombre chofer (parcial)" value={filtros.chofer} onChange={setF("chofer")}
               className="border border-slate-300 rounded px-2 py-2 text-sm md:col-span-2" data-testid="filtro-chofer"/>
        <input placeholder="Pipa #" value={filtros.pipa} onChange={setF("pipa")}
               className="border border-slate-300 rounded px-2 py-2 text-sm" data-testid="filtro-pipa"/>
        <input type="date" value={filtros.desde} onChange={setF("desde")}
               className="border border-slate-300 rounded px-2 py-2 text-sm" data-testid="filtro-desde"/>
        <input type="date" value={filtros.hasta} onChange={setF("hasta")}
               className="border border-slate-300 rounded px-2 py-2 text-sm" data-testid="filtro-hasta"/>
        <div className="md:col-span-5 flex justify-end">
          <button type="submit" disabled={loading} data-testid="btn-aplicar-filtros"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded px-4 py-2 text-sm disabled:opacity-60">
            <i className="fa-solid fa-magnifying-glass mr-1"/> {loading ? "Cargando…" : "Aplicar filtros"}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="text-sm text-slate-500 py-4">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-500 py-4" data-testid="actividad-empty">
          Sin eventos para los filtros seleccionados.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="actividad-tabla">
            <thead className="bg-slate-50 text-slate-600 uppercase">
              <tr>
                <th className="px-2 py-2 text-left">Fecha</th>
                <th className="px-2 py-2 text-left">Chofer</th>
                <th className="px-2 py-2 text-center">Pipa reportada</th>
                <th className="px-2 py-2 text-left">Tipo evento</th>
                <th className="px-2 py-2 text-center">Tipo</th>
                <th className="px-2 py-2 text-left">Lugar atendido</th>
                <th className="px-2 py-2 text-center">Estado</th>
                <th className="px-2 py-2 text-center">Foto</th>
                <th className="px-2 py-2 text-center">Evidencia</th>
                <th className="px-2 py-2 text-left">GPS</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => {
                const fotos = e.fotos_data || [];
                const fotoPrincipal = fotos[0];
                const estadoBadge = ESTADO_BADGE[e.estado];
                const esPendiente = e.estado === "PENDIENTE" || !e.estado;
                return (
                  <tr key={e.id} className="border-t border-slate-100">
                    <td className="px-2 py-2 mono">
                      {fmtChih(e.timestamp)}
                      {e.folio && (
                        <div style={{ fontSize: 10, color: "#64748B", marginTop: 2, fontFamily: "monospace", fontWeight: 600 }}>
                          {e.folio}
                        </div>
                      )}
                      {esAdmin && (
                        <div
                          title={`ID: ${e.id} — clic para copiar`}
                          onClick={() => { navigator.clipboard.writeText(e.id); toast.success("ID copiado"); }}
                          style={{ fontSize: 10, color: "#CBD5E1", marginTop: 2, cursor: "pointer", fontFamily: "monospace", letterSpacing: 0 }}
                        >
                          {e.id?.slice(0, 8)}…
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2">{e.chofer_nombre || "—"}</td>
                    <td className="px-2 py-2 text-center">{e.pipa_reportada || e.pipa_id || <span className="text-slate-400">—</span>}</td>
                    <td className="px-2 py-2">
                      {e.tipo === "CAMELLÓN"
                        ? <span style={{background:"#DCFCE7",color:"#15803D",fontWeight:700,padding:"2px 8px",borderRadius:999,fontSize:11,whiteSpace:"nowrap"}}>CAMELLÓN</span>
                        : e.tipo === "LLENADO DE FUENTE"
                        ? <span style={{background:"#DBEAFE",color:"#1E40AF",fontWeight:700,padding:"2px 8px",borderRadius:999,fontSize:11,whiteSpace:"nowrap"}}>LLENADO DE FUENTE</span>
                        : (TIPO_EVENTO_LABEL[e.tipo] || e.tipo || "—")}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {e.tipo === "crc_oficio"
                        ? (
                          editandoResultado?.id === e.id
                            ? (
                              <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-start",minWidth:160}}>
                                <div style={{display:"flex",gap:4}}>
                                  <button
                                    onClick={() => setEditandoResultado(p => ({...p, resultado:"ATENDIDO"}))}
                                    style={{padding:"2px 8px",borderRadius:999,fontSize:11,fontWeight:700,border:"2px solid",
                                      borderColor: editandoResultado.resultado==="ATENDIDO" ? "#15803D" : "#CBD5E1",
                                      background: editandoResultado.resultado==="ATENDIDO" ? "#DCFCE7" : "#F8FAFC",
                                      color: editandoResultado.resultado==="ATENDIDO" ? "#15803D" : "#64748B",
                                      cursor:"pointer"}}>
                                    ✅ Atendido
                                  </button>
                                  <button
                                    onClick={() => setEditandoResultado(p => ({...p, resultado:"NO_ATENDIDO"}))}
                                    style={{padding:"2px 8px",borderRadius:999,fontSize:11,fontWeight:700,border:"2px solid",
                                      borderColor: editandoResultado.resultado==="NO_ATENDIDO" ? "#B91C1C" : "#CBD5E1",
                                      background: editandoResultado.resultado==="NO_ATENDIDO" ? "#FEE2E2" : "#F8FAFC",
                                      color: editandoResultado.resultado==="NO_ATENDIDO" ? "#B91C1C" : "#64748B",
                                      cursor:"pointer"}}>
                                    ❌ No atendido
                                  </button>
                                </div>
                                {editandoResultado.resultado === "NO_ATENDIDO" && (
                                  <input
                                    autoFocus
                                    placeholder="Motivo (obligatorio)"
                                    value={editandoResultado.motivo}
                                    onChange={ev => setEditandoResultado(p => ({...p, motivo: ev.target.value}))}
                                    onKeyDown={ev => { if (ev.key === "Enter") guardarResultado(); if (ev.key === "Escape") setEditandoResultado(null); }}
                                    style={{padding:"2px 6px",borderRadius:5,border:"1px solid #94A3B8",fontSize:11,width:156,outline:"none"}}
                                  />
                                )}
                                <div style={{display:"flex",gap:4}}>
                                  <button onClick={guardarResultado} disabled={guardandoResultado}
                                    style={{padding:"2px 10px",borderRadius:5,background:"#1D4ED8",color:"#fff",border:"none",cursor:"pointer",fontSize:11,fontWeight:700,opacity:guardandoResultado?0.6:1}}>
                                    {guardandoResultado ? "…" : "Guardar"}
                                  </button>
                                  <button onClick={() => setEditandoResultado(null)}
                                    style={{padding:"2px 8px",borderRadius:5,background:"#E2E8F0",color:"#475569",border:"none",cursor:"pointer",fontSize:11}}>
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            )
                            : (
                              <div>
                                <span
                                  onClick={puedeCorregirResultado ? () => setEditandoResultado({
                                    id: e.id,
                                    resultado: e.estado === "NO_LOCALIZADO" ? "NO_ATENDIDO" : "ATENDIDO",
                                    motivo: e.motivo_no_atendido || "",
                                  }) : undefined}
                                  title={puedeCorregirResultado ? "Clic para corregir resultado" : undefined}
                                  style={{background:"#DBEAFE",color:"#1D4ED8",fontWeight:700,padding:"2px 8px",borderRadius:999,fontSize:11,whiteSpace:"nowrap",
                                    cursor: puedeCorregirResultado ? "pointer" : "default",
                                    borderBottom: puedeCorregirResultado ? "1px dashed #93C5FD" : "none",
                                    display:"inline-block"}}>
                                  {e.referencia_folio || "CRC"}
                                </span>
                                <div style={{fontSize:10,marginTop:2,fontWeight:600,
                                  color: e.estado === "NO_LOCALIZADO" ? "#B91C1C" : "#15803D"}}>
                                  {e.estado === "NO_LOCALIZADO" ? "❌ No atendido" : e.estado === "ATENDIDO" ? "✅ Atendido" : ""}
                                </div>
                                {e.estado === "NO_LOCALIZADO" && e.motivo_no_atendido && (
                                  <div className="text-[10px] text-slate-500 mt-0.5 max-w-[110px] truncate" title={e.motivo_no_atendido}>
                                    {e.motivo_no_atendido}
                                  </div>
                                )}
                              </div>
                            )
                        )
                        : (
                          editandoResultado?.id === e.id
                            ? (
                              <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-start",minWidth:160}}>
                                <div style={{display:"flex",gap:4}}>
                                  <button
                                    onClick={() => setEditandoResultado(p => ({...p, resultado:"ATENDIDO"}))}
                                    style={{padding:"2px 8px",borderRadius:999,fontSize:11,fontWeight:700,border:"2px solid",
                                      borderColor: editandoResultado.resultado==="ATENDIDO" ? "#15803D" : "#CBD5E1",
                                      background: editandoResultado.resultado==="ATENDIDO" ? "#DCFCE7" : "#F8FAFC",
                                      color: editandoResultado.resultado==="ATENDIDO" ? "#15803D" : "#64748B",
                                      cursor:"pointer"}}>
                                    ✅ Atendido
                                  </button>
                                  <button
                                    onClick={() => setEditandoResultado(p => ({...p, resultado:"NO_ATENDIDO"}))}
                                    style={{padding:"2px 8px",borderRadius:999,fontSize:11,fontWeight:700,border:"2px solid",
                                      borderColor: editandoResultado.resultado==="NO_ATENDIDO" ? "#B91C1C" : "#CBD5E1",
                                      background: editandoResultado.resultado==="NO_ATENDIDO" ? "#FEE2E2" : "#F8FAFC",
                                      color: editandoResultado.resultado==="NO_ATENDIDO" ? "#B91C1C" : "#64748B",
                                      cursor:"pointer"}}>
                                    ❌ No atendido
                                  </button>
                                </div>
                                {editandoResultado.resultado === "NO_ATENDIDO" && (
                                  <input
                                    autoFocus
                                    placeholder="Motivo (obligatorio)"
                                    value={editandoResultado.motivo}
                                    onChange={ev => setEditandoResultado(p => ({...p, motivo: ev.target.value}))}
                                    onKeyDown={ev => { if (ev.key === "Enter") guardarResultado(); if (ev.key === "Escape") setEditandoResultado(null); }}
                                    style={{padding:"2px 6px",borderRadius:5,border:"1px solid #94A3B8",fontSize:11,width:156,outline:"none"}}
                                  />
                                )}
                                <div style={{display:"flex",gap:4}}>
                                  <button onClick={guardarResultado} disabled={guardandoResultado}
                                    style={{padding:"2px 10px",borderRadius:5,background:"#1D4ED8",color:"#fff",border:"none",cursor:"pointer",fontSize:11,fontWeight:700,opacity:guardandoResultado?0.6:1}}>
                                    {guardandoResultado ? "…" : "Guardar"}
                                  </button>
                                  <button onClick={() => setEditandoResultado(null)}
                                    style={{padding:"2px 10px",borderRadius:5,background:"#F1F5F9",color:"#475569",border:"none",cursor:"pointer",fontSize:11}}>
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            )
                            : (
                              puedeCorregirResultado
                                ? (
                                  <button
                                    onClick={() => setEditandoResultado({
                                      id: e.id,
                                      resultado: (e.atendido === false) ? "NO_ATENDIDO" : "ATENDIDO",
                                      motivo: e.motivo_no_atendido || "",
                                    })}
                                    title="Corregir resultado"
                                    style={{background:"#F1F5F9",color:"#475569",fontWeight:600,padding:"2px 8px",borderRadius:999,fontSize:11,border:"none",cursor:"pointer"}}>
                                    {e.atendido === false ? "❌ Ordinaria" : "✅ Ordinaria"}
                                  </button>
                                )
                                : <span style={{background:"#F1F5F9",color:"#475569",fontWeight:600,padding:"2px 8px",borderRadius:999,fontSize:11}}>Ordinaria</span>
                            )
                        )}
                    </td>
                    <td className="px-2 py-2">
                      {puedeGestionar && editandoLugar?.id === e.id ? (
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <input
                            autoFocus
                            value={editandoLugar.valor}
                            onChange={ev => setEditandoLugar(p => ({ ...p, valor: ev.target.value }))}
                            onKeyDown={ev => { if (ev.key === "Enter") guardarLugar(); if (ev.key === "Escape") setEditandoLugar(null); }}
                            style={{ padding: "2px 6px", borderRadius: 5, border: "1px solid #94A3B8", fontSize: 12, width: 160, outline: "none" }}
                          />
                          <button onClick={guardarLugar} disabled={guardandoLugar}
                            style={{ width: 24, height: 24, borderRadius: 5, background: "#15803D", color: "#fff", border: "none", cursor: "pointer", fontSize: 11, display:"flex", alignItems:"center", justifyContent:"center" }}>
                            <i className="fa-solid fa-check" />
                          </button>
                          <button onClick={() => setEditandoLugar(null)}
                            style={{ width: 24, height: 24, borderRadius: 5, background: "#E2E8F0", color: "#475569", border: "none", cursor: "pointer", fontSize: 11, display:"flex", alignItems:"center", justifyContent:"center" }}>
                            <i className="fa-solid fa-xmark" />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <span
                            onClick={puedeGestionar ? () => setEditandoLugar({ id: e.id, valor: e.lugar_atendido || e.nombre_lugar || "" }) : undefined}
                            style={puedeGestionar ? { cursor: "text", borderBottom: "1px dashed #CBD5E1", display: "inline-block", minWidth: 40 } : {}}
                            title={puedeGestionar ? "Clic para editar nombre" : undefined}
                          >
                            {(e.lugar_atendido || e.nombre_lugar) || <span className="text-slate-400">—</span>}
                          </span>
                          {puedeGestionar && e.espacio_id && (
                            e.necesita_clasificacion ? (
                              clasificandoEspacio === e.id ? (
                                <div style={{ marginTop: 3, display: "flex", gap: 4, alignItems: "center" }}>
                                  <select
                                    autoFocus
                                    defaultValue=""
                                    disabled={guardandoTipo}
                                    onChange={ev => ev.target.value && guardarTipoEspacio(e, ev.target.value)}
                                    style={{ fontSize: 11, padding: "1px 4px", borderRadius: 4, border: "1px solid #94A3B8", maxWidth: 160 }}
                                  >
                                    <option value="" disabled>Tipo de espacio…</option>
                                    {TIPOS_ESPACIO_OPCIONES.map(t => <option key={t} value={t}>{t}</option>)}
                                  </select>
                                  <button onClick={() => setClasificandoEspacio(null)}
                                    style={{ width: 20, height: 20, borderRadius: 4, background: "#E2E8F0", color: "#475569", border: "none", cursor: "pointer", fontSize: 10 }}>
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <div>
                                  <button
                                    onClick={() => setClasificandoEspacio(e.id)}
                                    title="Clasificar tipo de espacio"
                                    style={{ fontSize: 10, color: "#B45309", background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 4, padding: "1px 6px", cursor: "pointer", marginTop: 2, display: "inline-block" }}
                                  >
                                    ⚑ Sin tipo — clasificar
                                  </button>
                                </div>
                              )
                            ) : (
                              <div style={{ fontSize: 10, color: "#64748B", marginTop: 1 }}>
                                {e.tipo_espacio_lugar}
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {estadoBadge
                        ? <span style={{ background: estadoBadge.bg, color: estadoBadge.fg, padding: "2px 8px", borderRadius: 999, fontWeight: 700, fontSize: 11 }}>
                            {estadoBadge.label}
                          </span>
                        : <span className="text-slate-400 text-xs">—</span>}
                      {e.comentario_admin && (
                        <div className="text-slate-500 text-[10px] mt-0.5 max-w-[120px] truncate" title={e.comentario_admin}>
                          {e.comentario_admin}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {fotoPrincipal
                        ? (
                          <button
                            onClick={() => setFotoModal({ url: fotoPrincipal, fotos, chofer: e.chofer_nombre, fecha: e.timestamp })}
                            data-testid={`btn-foto-${e.id}`}
                            className="inline-block hover:ring-2 hover:ring-indigo-500 rounded overflow-hidden align-middle"
                            title="Ver imagen completa"
                          >
                            <img src={fotoPrincipal} alt="evidencia" loading="lazy"
                                 style={{ width: 48, height: 48, objectFit: "cover", display: "block" }}/>
                            {fotos.length > 1 && (
                              <span className="text-[10px] text-indigo-600 font-bold">+{fotos.length - 1}</span>
                            )}
                          </button>
                        )
                        : e.tiene_foto
                          ? <span className="text-emerald-700"><i className="fa-solid fa-circle-check"/> Sí</span>
                          : <span className="text-slate-400"><i className="fa-solid fa-circle-xmark"/> No</span>}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => {
                          const base = window.location.origin;
                          const url = e.tipo === "crc_oficio" && e.referencia_folio
                            ? `${base}/evidencia/${encodeURIComponent(e.referencia_folio)}`
                            : `${base}/evidencia-evento/${encodeURIComponent(e.folio || e.id)}`;
                          navigator.clipboard.writeText(url);
                          toast.success("Link copiado");
                        }}
                        title="Copiar link de evidencia"
                        style={{background:"none",border:"none",cursor:"pointer",color:"#0EA5E9",fontSize:16,padding:4}}
                        data-testid={`btn-copiar-link-${e.id}`}
                      >
                        <i className="fa-solid fa-link"/>
                      </button>
                    </td>
                    <td className="px-2 py-2 mono">
                      {e.gps_lat != null && e.gps_lon != null
                        ? <a href={`https://www.google.com/maps?q=${e.gps_lat},${e.gps_lon}`} target="_blank" rel="noopener noreferrer"
                             className="text-blue-600 hover:underline">
                            {e.gps_lat.toFixed(5)}, {e.gps_lon.toFixed(5)}
                          </a>
                        : <span className="text-slate-400">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}


      {/* ── Modal Foto ─────────────────────────────────────── */}
      {fotoModal && (
        <div
          onClick={() => setFotoModal(null)}
          data-testid="modal-foto-evento"
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 9999, padding: 16, cursor: "zoom-out",
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: "90vw", maxHeight: "90vh", cursor: "default" }}>
            <img src={fotoModal.url} alt="evidencia ampliada"
                 style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: 8, display: "block" }}/>
            <div className="flex items-center justify-between mt-3 text-white text-xs">
              <div>
                <div className="font-bold">{fotoModal.chofer}</div>
                <div className="opacity-80">{(fotoModal.fecha || "").slice(0,19).replace("T"," ")}</div>
              </div>
              <div className="flex gap-2">
                <a href={fotoModal.url} target="_blank" rel="noopener noreferrer"
                   className="bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded font-bold">
                  <i className="fa-solid fa-up-right-from-square mr-1"/> Abrir en nueva pestaña
                </a>
                <button onClick={() => setFotoModal(null)}
                        className="bg-white text-slate-900 px-3 py-1 rounded font-bold">
                  Cerrar
                </button>
              </div>
            </div>
            {fotoModal.fotos.length > 1 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {fotoModal.fotos.map((u, i) => (
                  <button key={i} onClick={() => setFotoModal(p => ({ ...p, url: u }))}>
                    <img src={u} alt={`f${i}`}
                         style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 4,
                                  border: u === fotoModal.url ? "2px solid #C9A84C" : "2px solid transparent" }}/>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TAB — Catálogo de Espacios (solo admin)
   ═══════════════════════════════════════════════════════════ */
const ESTADOS_GPS = ["", "CAPTURADO", "PENDIENTE", "SIN_GPS"];

function TabCatalogoEspacios({ user }) {
  const token = localStorage.getItem("dmu_token"); // clave correcta de AuthContext

  const [q, setQ]               = useState("");
  const [zona, setZona]         = useState("");
  const [estadoGps, setEstadoGps] = useState("");
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [offset, setOffset]     = useState(0);
  const [cargando, setCargando] = useState(false);
  const LIMIT = 50;

  // Celda en edición: { id, campo, valor }
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async (off = 0) => {
    setCargando(true);
    try {
      const r = await axios.get(`${API}/admin/catalogo-espacios`, {
        params: { q, zona, estado_gps: estadoGps, limit: LIMIT, offset: off },
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setOffset(off);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCargando(false);
    }
  }, [q, zona, estadoGps, token]);

  useEffect(() => { cargar(0); }, []);   // carga inicial

  const buscar = (e) => { e.preventDefault(); cargar(0); };

  const iniciarEdicion = (item, campo) => {
    setEditando({ id: item.id, campo, valor: item[campo] ?? "" });
  };

  const guardarEdicion = async () => {
    if (!editando) return;
    setGuardando(true);
    try {
      await axios.patch(
        `${API}/admin/catalogo-espacios/${editando.id}`,
        { [editando.campo]: editando.valor.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Guardado");
      setItems(prev => prev.map(it =>
        it.id === editando.id ? { ...it, [editando.campo]: editando.valor.trim() } : it
      ));
      setEditando(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setGuardando(false);
    }
  };

  const cancelarEdicion = () => setEditando(null);

  const CAMPOS_EDITABLES = ["nombre", "zona", "colonia", "entre_calles"];

  return (
    <div className="sup-pane" data-testid="tab-catalogo-panel">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <h2 className="sup-h2" style={{ margin: 0 }}>
          <i className="fa-solid fa-map-pin mr-2" />Catálogo de Espacios
        </h2>
        <span style={{ fontSize: 12, color: "#94A3B8" }}>{total.toLocaleString()} espacios en total</span>
      </div>

      {/* Buscador */}
      <form onSubmit={buscar} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Buscar por nombre…"
          style={S.input}
        />
        <input
          value={zona}
          onChange={e => setZona(e.target.value)}
          placeholder="Zona (ej: Z1, CAMELLONES)"
          style={{ ...S.input, maxWidth: 180 }}
        />
        <select value={estadoGps} onChange={e => setEstadoGps(e.target.value)} style={S.select}>
          <option value="">Todos los GPS</option>
          <option value="CAPTURADO">CAPTURADO</option>
          <option value="PENDIENTE">PENDIENTE</option>
          <option value="SIN_GPS">SIN_GPS</option>
        </select>
        <button type="submit" style={S.btnPrimary} disabled={cargando}>
          <i className="fa-solid fa-magnifying-glass mr-1" />
          {cargando ? "Buscando…" : "Buscar"}
        </button>
      </form>

      <p style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>
        Haz clic en cualquier celda de <strong>nombre</strong>, <strong>zona</strong>, <strong>colonia</strong> o <strong>entre calles</strong> para editarla directamente.
      </p>

      {/* Tabla */}
      <div style={{ overflowX: "auto" }}>
        <table style={S.tabla}>
          <thead>
            <tr>
              {["id_oficial", "Nombre", "Zona", "Colonia", "Entre calles", "Lat", "Lon", "GPS"].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !cargando && (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 24, color: "#94A3B8" }}>
                Sin resultados — ajusta la búsqueda
              </td></tr>
            )}
            {items.map(it => (
              <tr key={it.id} style={S.tr}>
                <td style={S.td}>{it.id_oficial ?? <span style={S.vacío}>—</span>}</td>
                {["nombre", "zona", "colonia", "entre_calles"].map(campo => {
                  const activo = editando?.id === it.id && editando?.campo === campo;
                  return (
                    <td key={campo} style={{ ...S.td, minWidth: campo === "nombre" ? 200 : 100 }}>
                      {activo ? (
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <input
                            autoFocus
                            value={editando.valor}
                            onChange={e => setEditando(ev => ({ ...ev, valor: e.target.value }))}
                            onKeyDown={e => { if (e.key === "Enter") guardarEdicion(); if (e.key === "Escape") cancelarEdicion(); }}
                            style={{ ...S.inputInline, width: campo === "nombre" ? 190 : 100 }}
                          />
                          <button onClick={guardarEdicion} disabled={guardando} style={S.btnGuardar} title="Guardar">
                            <i className="fa-solid fa-check" />
                          </button>
                          <button onClick={cancelarEdicion} style={S.btnCancelar} title="Cancelar">
                            <i className="fa-solid fa-xmark" />
                          </button>
                        </div>
                      ) : (
                        <span
                          onClick={() => iniciarEdicion(it, campo)}
                          style={{ cursor: "text", borderBottom: "1px dashed #CBD5E1", display: "inline-block", minWidth: 40 }}
                          title="Clic para editar"
                        >
                          {it[campo] || <span style={S.vacío}>—</span>}
                        </span>
                      )}
                    </td>
                  );
                })}
                <td style={{ ...S.td, fontFamily: "mono", fontSize: 11 }}>{it.gps_lat ?? "—"}</td>
                <td style={{ ...S.td, fontFamily: "mono", fontSize: 11 }}>{it.gps_lon ?? "—"}</td>
                <td style={S.td}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                    background: it.estado_gps === "CAPTURADO" ? "#DCFCE7" : "#FEF3C7",
                    color:      it.estado_gps === "CAPTURADO" ? "#15803D" : "#92400E",
                  }}>{it.estado_gps ?? "—"}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {total > LIMIT && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 14, justifyContent: "center" }}>
          <button
            onClick={() => cargar(Math.max(0, offset - LIMIT))}
            disabled={offset === 0 || cargando}
            style={S.btnPage}
          >
            <i className="fa-solid fa-chevron-left" /> Anterior
          </button>
          <span style={{ fontSize: 13, color: "#64748B" }}>
            {offset + 1}–{Math.min(offset + LIMIT, total)} de {total.toLocaleString()}
          </span>
          <button
            onClick={() => cargar(offset + LIMIT)}
            disabled={offset + LIMIT >= total || cargando}
            style={S.btnPage}
          >
            Siguiente <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      )}

      {cargando && (
        <div style={{ textAlign: "center", padding: 32, color: "#64748B" }}>
          <i className="fa-solid fa-spinner fa-spin mr-2" />Cargando…
        </div>
      )}
    </div>
  );
}

const S = {
  input: { padding: "6px 10px", borderRadius: 7, border: "1px solid #E2E8F0", fontSize: 13, flex: 1, minWidth: 180, outline: "none" },
  select: { padding: "6px 10px", borderRadius: 7, border: "1px solid #E2E8F0", fontSize: 13, background: "white" },
  btnPrimary: { padding: "6px 14px", borderRadius: 7, background: "#0F172A", color: "white", border: "none", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" },
  btnPage: { padding: "5px 12px", borderRadius: 7, border: "1px solid #E2E8F0", background: "white", fontSize: 12, cursor: "pointer" },
  btnGuardar: { width: 26, height: 26, borderRadius: 6, background: "#15803D", color: "white", border: "none", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" },
  btnCancelar: { width: 26, height: 26, borderRadius: 6, background: "#E2E8F0", color: "#475569", border: "none", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { padding: "8px 10px", background: "#F8FAFC", borderBottom: "2px solid #E2E8F0", textAlign: "left", fontWeight: 700, fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap" },
  td: { padding: "8px 10px", borderBottom: "1px solid #F1F5F9", verticalAlign: "middle" },
  tr: { transition: "background .1s" },
  inputInline: { padding: "3px 6px", borderRadius: 5, border: "1px solid #94A3B8", fontSize: 13, outline: "none" },
  vacío: { color: "#CBD5E1" },
};

/* ─────────────────────────────────────────────────────────────
   TabRadiografiaParques — Octavio 21-ago-2026
   Pestaña ejecutiva: cobertura de riego por espacio público.
   Datos en vivo al abrir. Fuente: espacios_publicos × intervenciones.
   ───────────────────────────────────────────────────────────── */
function TabRadiografiaParques() {
  const [dias, setDias]           = useState(15);
  const [zona, setZona]           = useState("");
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [zonas, setZonas]         = useState([]);
  // ── controles de la tabla ──
  const [busqueda, setBusqueda]   = useState("");
  const [filtroRiegos, setFiltroRiegos] = useState("todos");   // "todos"|"0"|"1"|"2"|"3"|"4"|"5"|"6+"
  const [soloSinRiego, setSoloSinRiego] = useState(false);
  const [pagina, setPagina]       = useState(1);
  const [porPagina, setPorPagina] = useState(15);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const cargar = useCallback(async () => {
    setLoading(true);
    setData(null);
    setPagina(1);
    try {
      const params = { dias };
      if (zona) params.zona_id = zona;
      const r = await axios.get(`${API}/espacios/radiografia-cobertura`, { params });
      setData(r.data);
      if (!zonas.length) {
        const zs = [...new Set((r.data.espacios || []).map(e => e.zona_id).filter(Boolean))].sort();
        setZonas(zs);
      }
    } catch (e) {
      toast.error("Error cargando radiografía: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [dias, zona]);

  useEffect(() => { cargar(); }, [cargar]);

  // Resetear página cuando cambia un filtro secundario
  useEffect(() => { setPagina(1); }, [busqueda, filtroRiegos, soloSinRiego, porPagina]);

  const fmtFecha = (iso) => {
    if (!iso) return <span style={{ color: "#CBD5E1" }}>Nunca registrado</span>;
    return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
  };

  const pillDias = (d) => {
    if (d === null || d === undefined) return <span style={{ background: "#FEE2E2", color: "#991B1B", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700 }}>Sin registro</span>;
    if (d >= 15) return <span style={{ background: "#FEE2E2", color: "#991B1B", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700 }}>{d}d</span>;
    if (d >= 7)  return <span style={{ background: "#FEF3C7", color: "#92400E", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700 }}>{d}d</span>;
    return <span style={{ background: "#D1FAE5", color: "#047857", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700 }}>{d}d</span>;
  };

  const res = data?.resumen;
  const todosEspacios = data?.espacios || [];

  // ── Aplicar filtros combinados ──
  const bq = busqueda.trim().toLowerCase();
  const filtrados = todosEspacios.filter(e => {
    if (soloSinRiego && e.veces_30d > 0) return false;
    if (filtroRiegos !== "todos") {
      const n = parseInt(filtroRiegos);
      if (filtroRiegos === "6+") { if (e.veces_30d < 6) return false; }
      else { if (e.veces_30d !== n) return false; }
    }
    if (bq) {
      const nombre   = (e.nombre || "").toLowerCase();
      const idOfic   = (e.id_oficial || "").toLowerCase();
      const coords   = `${e.gps_lat ?? ""} ${e.gps_lon ?? ""}`;
      const espId    = (e.espacio_id || "").toLowerCase();
      if (!nombre.includes(bq) && !idOfic.includes(bq) && !coords.includes(bq) && !espId.includes(bq)) return false;
    }
    return true;
  });

  // ── Paginación ──
  const totalPags = Math.max(1, Math.ceil(filtrados.length / porPagina));
  const pagActual = Math.min(pagina, totalPags);
  const inicio    = (pagActual - 1) * porPagina;
  const paginados = filtrados.slice(inicio, inicio + porPagina);

  return (
    <div style={{ padding: "20px 24px" }}>
      {/* ── Nota ejecutiva + periodo analizado ── */}
      <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#1E40AF" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: res ? 8 : 0 }}>
          <i className="fa-solid fa-circle-info" style={{ marginTop: 2, flexShrink: 0 }}/>
          <span>
            <strong>Contexto ejecutivo:</strong> Esta brecha de cobertura refleja la capacidad operativa disponible
            (pipas vs. espacios a cubrir), no una falta de atención. Con las pipas disponibles se atiende el máximo
            posible de acuerdo a la ruta y prioridades del día.
          </span>
        </div>
        {res && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 24, fontSize: 12, color: "#1D4ED8", fontWeight: 600 }}>
            <i className="fa-solid fa-calendar-range" style={{ opacity: 0.7 }}/>
            Periodo analizado (riegos en columna "Últimos 30d"):&nbsp;
            <span style={{ fontFamily: "monospace" }}>
              {new Date(res.periodo_desde + "T12:00:00").toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" })}
            </span>
            &nbsp;→&nbsp;
            <span style={{ fontFamily: "monospace" }}>
              {new Date(res.periodo_hasta + "T12:00:00").toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" })}
            </span>
            &nbsp;(30 días)
          </div>
        )}
      </div>

      {/* ── Filtros de carga (umbral + zona + actualizar) ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20, alignItems: "flex-end" }}>
        <div>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748B", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Días sin riego (umbral)
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="number" min={1} max={365} value={dias}
              onChange={e => setDias(Math.max(1, parseInt(e.target.value) || 15))}
              style={{ width: 70, padding: "6px 8px", border: "1px solid #E2E8F0", borderRadius: 7, fontSize: 13 }}
            />
            <span style={{ fontSize: 12, color: "#64748B" }}>días</span>
          </div>
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748B", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Zona
          </label>
          <select value={zona} onChange={e => setZona(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #E2E8F0", borderRadius: 7, fontSize: 13, background: "white" }}>
            <option value="">Todas las zonas</option>
            {zonas.map(z => <option key={z} value={z}>{z}</option>)}
          </select>
        </div>
        <button
          onClick={cargar} disabled={loading}
          style={{ padding: "6px 16px", background: "#0F172A", color: "white", border: 0, borderRadius: 7, fontSize: 13, cursor: "pointer" }}
        >
          <i className="fa-solid fa-rotate-right mr-1"/>{loading ? "Cargando…" : "Actualizar"}
        </button>
      </div>

      {/* ── Tarjetas de resumen ── */}
      {res && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 24 }}>
          <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 12, padding: "16px 20px" }}>
            <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
              <i className="fa-solid fa-map-pin mr-1"/>Total espacios
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: "#0F172A" }}>{res.total_espacios.toLocaleString("es-MX")}</div>
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>registrados en catálogo</div>
          </div>
          <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 12, padding: "16px 20px" }}>
            <div style={{ fontSize: 11, color: "#991B1B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
              <i className="fa-solid fa-triangle-exclamation mr-1"/>Sin riego &gt;{res.dias_umbral}d
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: "#DC2626" }}>{res.sin_riego_en_n_dias.toLocaleString("es-MX")}</div>
            <div style={{ fontSize: 12, color: "#EF4444", marginTop: 2 }}>
              {res.total_espacios ? Math.round(res.sin_riego_en_n_dias / res.total_espacios * 100) : 0}% del catálogo
            </div>
          </div>
          {/* Capacidad operativa con desglose */}
          <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 12, padding: "16px 20px" }}>
            <div style={{ fontSize: 11, color: "#1E40AF", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
              <i className="fa-solid fa-truck-droplet mr-1"/>Capacidad operativa
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#1D4ED8", lineHeight: 1.1 }}>
              {res.pipas_operativas}
              <span style={{ fontSize: 14, fontWeight: 400, color: "#64748B" }}> / {res.total_pipas} pipas</span>
            </div>
            <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span style={{ background: "#DCFCE7", color: "#166534", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 99 }}>
                {res.pipas_operativas} operativas
              </span>
              <span style={{ background: "#FEF9C3", color: "#854D0E", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 99 }}>
                {res.pipas_en_taller ?? (res.total_pipas - res.pipas_operativas)} en taller
              </span>
            </div>
            <div style={{ fontSize: 12, color: "#3B82F6", marginTop: 6 }}>
              {res.total_espacios ? (res.total_espacios / Math.max(res.pipas_operativas, 1)).toFixed(0) : "—"} espacios por pipa operativa
            </div>
          </div>
        </div>
      )}

      {/* ── Controles de tabla: buscador + filtro riegos + toggle ── */}
      {todosEspacios.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 12, padding: "10px 14px", background: "#F8FAFC", borderRadius: 10, border: "1px solid #E2E8F0" }}>
          {/* Buscador */}
          <div style={{ position: "relative", flexGrow: 1, minWidth: 200 }}>
            <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "#94A3B8", fontSize: 12 }}/>
            <input
              type="text"
              placeholder="Buscar por nombre, ID, coordenadas…"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              style={{ width: "100%", paddingLeft: 28, paddingRight: 8, paddingTop: 6, paddingBottom: 6, border: "1px solid #E2E8F0", borderRadius: 7, fontSize: 13, background: "white", boxSizing: "border-box" }}
            />
            {busqueda && (
              <button onClick={() => setBusqueda("")} style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: 0, cursor: "pointer", color: "#94A3B8", fontSize: 13 }}>✕</button>
            )}
          </div>
          {/* Filtro por número de riegos */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748B", marginRight: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>Riegos 30d:</label>
            <select value={filtroRiegos} onChange={e => setFiltroRiegos(e.target.value)}
              style={{ padding: "5px 8px", border: "1px solid #E2E8F0", borderRadius: 7, fontSize: 13, background: "white" }}>
              {["todos","0","1","2","3","4","5","6+"].map(v => (
                <option key={v} value={v}>{v === "todos" ? "Todos" : v}</option>
              ))}
            </select>
          </div>
          {/* Toggle sin riego */}
          <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 13, color: "#374151", whiteSpace: "nowrap" }}>
            <div
              onClick={() => setSoloSinRiego(v => !v)}
              style={{
                width: 36, height: 20, borderRadius: 99, background: soloSinRiego ? "#DC2626" : "#D1D5DB",
                position: "relative", transition: "background 0.2s", cursor: "pointer", flexShrink: 0,
              }}
            >
              <div style={{
                width: 16, height: 16, borderRadius: 99, background: "white",
                position: "absolute", top: 2, left: soloSinRiego ? 18 : 2, transition: "left 0.2s",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }}/>
            </div>
            Ocultar con riego
          </label>
          {/* Resultados */}
          <span style={{ fontSize: 12, color: "#94A3B8", marginLeft: "auto" }}>
            {filtrados.length.toLocaleString("es-MX")} de {todosEspacios.length.toLocaleString("es-MX")} espacios
          </span>
        </div>
      )}

      {/* ── Tabla ── */}
      {loading && <div style={{ textAlign: "center", padding: 60, color: "#94A3B8" }}><i className="fa-solid fa-spinner fa-spin mr-2"/>Consultando base de datos…</div>}

      {!loading && todosEspacios.length === 0 && data && (
        <div style={{ textAlign: "center", padding: 60, color: "#94A3B8" }}>Sin espacios para los filtros seleccionados.</div>
      )}

      {!loading && filtrados.length === 0 && todosEspacios.length > 0 && (
        <div style={{ textAlign: "center", padding: 40, color: "#94A3B8" }}>
          <i className="fa-solid fa-filter mr-2"/>Ningún espacio coincide con los filtros activos.
        </div>
      )}

      {!loading && paginados.length > 0 && (
        <>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#F8FAFC", borderBottom: "2px solid #E2E8F0" }}>
                  {["ID Oficial","Espacio / Parque","Zona","Coordenadas","Último riego","Días sin riego","Riegos 30d"].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginados.map((e, i) => (
                  <tr key={e.espacio_id} style={{ borderBottom: "1px solid #F1F5F9", background: i % 2 === 0 ? "white" : "#FAFAFA" }}>
                    <td style={{ padding: "8px 12px", color: "#64748B", fontSize: 12, whiteSpace: "nowrap" }}>
                      {e.id_oficial
                        ? <code style={{ background: "#F1F5F9", padding: "1px 5px", borderRadius: 4, fontSize: 11 }}>{e.id_oficial}</code>
                        : <span style={{ color: "#CBD5E1" }}>—</span>
                      }
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      <div style={{ fontWeight: 600 }}>{e.nombre}</div>
                    </td>
                    <td style={{ padding: "8px 12px", color: "#475569" }}>{e.zona_id || "—"}</td>
                    <td style={{ padding: "8px 12px", fontSize: 11, color: "#64748B", whiteSpace: "nowrap" }}>
                      {e.gps_lat && e.gps_lon
                        ? <span style={{ fontFamily: "monospace" }}>{Number(e.gps_lat).toFixed(5)}, {Number(e.gps_lon).toFixed(5)}</span>
                        : <span style={{ color: "#CBD5E1" }}>Sin GPS</span>
                      }
                    </td>
                    <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{fmtFecha(e.ultima_fecha)}</td>
                    <td style={{ padding: "8px 12px" }}>{pillDias(e.dias_sin_riego)}</td>
                    <td style={{ padding: "8px 12px", color: "#475569", textAlign: "center" }}>
                      {e.veces_30d > 0
                        ? <strong style={{ color: "#047857" }}>{e.veces_30d}×</strong>
                        : <span style={{ color: "#CBD5E1" }}>0</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Paginación ── */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginTop: 14, fontSize: 13 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#64748B" }}>
              <span>Mostrar</span>
              <select value={porPagina} onChange={e => { setPorPagina(Number(e.target.value)); }}
                style={{ padding: "4px 8px", border: "1px solid #E2E8F0", borderRadius: 7, fontSize: 13, background: "white" }}>
                {[10, 15, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>por página · Página {pagActual} de {totalPags}</span>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                disabled={pagActual <= 1}
                onClick={() => setPagina(p => Math.max(1, p - 1))}
                style={{ padding: "5px 14px", border: "1px solid #E2E8F0", borderRadius: 7, background: pagActual <= 1 ? "#F8FAFC" : "white", color: pagActual <= 1 ? "#CBD5E1" : "#374151", cursor: pagActual <= 1 ? "default" : "pointer", fontSize: 13 }}
              >
                ← Anterior
              </button>
              <button
                disabled={pagActual >= totalPags}
                onClick={() => setPagina(p => Math.min(totalPags, p + 1))}
                style={{ padding: "5px 14px", border: "1px solid #E2E8F0", borderRadius: 7, background: pagActual >= totalPags ? "#F8FAFC" : "white", color: pagActual >= totalPags ? "#CBD5E1" : "#374151", cursor: pagActual >= totalPags ? "default" : "pointer", fontSize: 13 }}
              >
                Siguiente →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
